import React, { useState, useRef, useEffect, useCallback, useMemo, useDeferredValue, forwardRef, useImperativeHandle } from "react";
import { createPortal, flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensors,
  useSensor,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ImagePlus, RefreshCw, Sparkles, X, ArrowLeft, Trash2, Info, GripHorizontal, ChevronDown, ChevronUp, Check, Plus, Pencil, History, AlertTriangle, RotateCcw, Layers } from "lucide-react";
import { toast } from "sonner";
import { Deck, Card, DeckLang, StudyModes, LANG_FLAGS, LANG_NAMES, STUDY_LANGUAGE_CODES, GEMINI_LANG_NAMES } from "@/types/linguadeck";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { I18N_LANGUAGE_KEYS } from "@/i18n/i18n";
import { normalizeTestData } from "@/lib/testData";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { callGemini } from "@/lib/gemini";
import { generateDeckAiText } from "@/lib/deckAiClient";
import {
  isRetryableAiImageError,
  buildAiImagePrompt,
  getAiImageToastKey,
  requestAiImageUrl,
} from "@/lib/aiImageClient";
import ImportModal from "@/components/ImportModal";
import {
  extractTextFromSupportedFile,
  extractStructuredPairs,
  pairsToCardDrafts,
  parseFlashcardInput,
  parseFlashcardInputMulti,
  segmentTextForMultiTopicAI,
  type ParsedModule,
} from "@/lib/flashcardAutomation";

export type DeckInitialType = "words" | "cards" | "tests";

interface DeckModalProps {
  deck?: Deck; // edit mode if provided
  onSave: (deck: Omit<Deck, "id">, options?: { isFromAi?: boolean; navigateToStudy?: boolean }) => void;
  /** Called when multiple modules are created at once (AI multi-topic flow) */
  onSaveMultiple?: (decks: Omit<Deck, "id">[], options?: { isFromAi?: boolean; navigateToStudy?: boolean }) => void;
  onClose: () => void;
  /** Pre-select deck category when creating a new deck */
  initialType?: DeckInitialType;
  // AI props (optional — only needed when creating new decks)
  aiModel?: string;
  /** Pollinations API keys (multiple for failover) */
  pollinationsKey?: string[];
  /** Gemini API keys (multiple for failover) — used for AI Generation tab */
  geminiKey?: string[];
  /** Render as full page instead of modal (no overlay, Back button, centered layout) */
  asPage?: boolean;
  /** Language validation: show inline errors when save attempted with missing front/back lang */
  showValidationErrors?: boolean;
  setShowValidationErrors?: (show: boolean) => void;
}

// ─── AI helpers ───────────────────────────────────────────────────────────────

const CARDS_PER_BATCH = 3;

/**
 * Robustly extract JSON from LLM output. Strips markdown fences (```json ... ```)
 * and extracts the first complete JSON object or array.
 */
function extractJson(text: string): unknown {
  const trimmed = text.trim();
  // Strip leading/trailing fences when JSON is at start/end
  const fenced = trimmed
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```\s*$/g, "")
    .trim();
  // Also extract content from mid-text markdown block (e.g. "Here are cards:\n```json\n[...]\n```")
  const midBlock = trimmed.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  const candidates = [fenced, midBlock?.[1]?.trim(), trimmed].filter(Boolean) as string[];
  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      /* try next */
    }
  }
  const firstBrace = trimmed.indexOf("{");
  const firstBracket = trimmed.indexOf("[");
  const start = firstBrace >= 0 && (firstBracket < 0 || firstBrace < firstBracket) ? firstBrace : firstBracket;
  if (start >= 0) {
    const closer = trimmed[start] === "{" ? "}" : "]";
    let depth = 0;
    let end = -1;
    for (let i = start; i < trimmed.length; i++) {
      const c = trimmed[i];
      if (c === trimmed[start]) depth++;
      else if (c === closer) {
        depth--;
        if (depth === 0) {
          end = i;
          break;
        }
      }
    }
    if (end >= 0) {
      try {
        return JSON.parse(trimmed.slice(start, end + 1));
      } catch {
        /* fall through to throw */
      }
    }
  }
  throw new Error("Invalid JSON in AI response");
}

const AI_FETCH_TIMEOUT_MS = 90_000;
const USE_AI_PROXY = import.meta.env.PROD || !!import.meta.env.VITE_AI_PROXY_ENABLED;

async function callAI(prompt: string, pollinationsKeys: string | string[], aiModel: string, geminiKeys: string[] = []): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_FETCH_TIMEOUT_MS);
  try {
    const text = await generateDeckAiText(prompt, pollinationsKeys, aiModel, geminiKeys, controller.signal);
    clearTimeout(timeoutId);
    if (!text || text.trim().length < 2) throw new Error("AI returned empty response");
    return text;
  } catch (e: unknown) {
    clearTimeout(timeoutId);
    const err = e instanceof Error ? e : new Error(String(e));
    if (err.name === "AbortError") throw new Error("Request timed out. Try fewer cards per batch.");
    throw err;
  }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

// ─── Suggestions helper ───────────────────────────────────────────────────

const SUGGEST_DEBOUNCE_MS = 300;
const SUGGESTIONS_LIMIT = 3;
const DRAFT_KEY = "lingua-deck-draft";

type AutomationPrefill = {
  cards: Card[];
  title?: string;
  lang?: string;
  level?: string;
  sourceLang?: string;
  targetLang?: string;
  count?: number;
  sourceSignature: string;
  sourceKind: "ai" | "parsed";
  autoGenerateImages?: boolean;
  /** Multi-module mode: when set, multiple decks will be created */
  modules?: Array<{
    title: string;
    subtitle?: string;
    cards: Card[];
    sourceLang?: string;
    targetLang?: string;
  }>;
};

/** Normalize draft from localStorage: support title/description/cards and alternate keys (items, term/definition, question/answer). */
function normalizeDraftFromStorage(raw: string): { title: string; description: string; cards: Card[] } | null {
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const title = String(parsed?.title ?? (parsed?.data as Record<string, unknown>)?.title ?? "").trim();
    const description = String(parsed?.description ?? (parsed?.data as Record<string, unknown>)?.description ?? "").trim();
    const rawCards = Array.isArray(parsed?.cards)
      ? parsed.cards
      : Array.isArray(parsed?.items)
        ? parsed.items
        : Array.isArray((parsed?.data as Record<string, unknown>)?.cards)
          ? (parsed.data as { cards: unknown[] }).cards
          : [];
    const cards: Card[] =
      rawCards.length > 0
        ? rawCards.map((c: Record<string, unknown>) => ({
            front: String(c.front ?? c.term ?? c.question ?? "").trim(),
            back: String(c.back ?? c.definition ?? c.answer ?? "").trim(),
            frontLang: String(c.frontLang ?? "").trim() || "",
            backLang: String(c.backLang ?? "").trim() || "",
            hint: String(c.hint ?? "").trim() || "",
            imageUrl: (c.imageUrl ?? null) as string | null | undefined,
            imagePrompt: c.imagePrompt as string | undefined,
          }))
        : [{ front: "", back: "", frontLang: "", backLang: "", hint: "" }];
    return { title, description, cards };
  } catch {
    return null;
  }
}

type SuggestionField = "front" | "back";

async function fetchSuggestions(text: string, field: SuggestionField): Promise<string[]> {
  const t = text.trim();
  if (!t || t.length < 2) return [];
  try {
    const col = field === "front" ? "front" : "back";
    const escaped = t.replace(/[%_\\]/g, (c) => (c === "\\" ? "\\\\" : "\\" + c));
    const { data, error } = await supabase
      .from("cards")
      .select(col)
      .ilike(col, `${escaped}%`)
      .limit(15);
    if (error) return [];
    const raw = (data ?? []).map((r) => String((r as Record<string, unknown>)[col] ?? "").trim()).filter(Boolean);
    const unique = Array.from(new Set(raw));
    return unique.slice(0, SUGGESTIONS_LIMIT);
  } catch {
    return [];
  }
}

// ─── Manual Tab ───────────────────────────────────────────────────────────────

const INITIAL_TYPE_TO_DECK_TYPE: Record<"words" | "cards" | "tests", string> = {
  words: "vocab",
  cards: "other",
  tests: "tests",
};

function LanguageSelector({
  value,
  onChange,
  placeholder,
  compact = false,
}: {
  value: string;
  onChange: (code: string | undefined) => void;
  placeholder: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const displayText = value ? LANG_NAMES[value as keyof typeof LANG_NAMES] ?? value : placeholder;
  const handleSelect = (code: string | undefined) => {
    onChange(code);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-gray-500 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400 transition-colors cursor-pointer focus:outline-none focus:ring-0 ${compact ? "text-[11px] mt-1" : "text-sm mt-1"}`}
        >
          <span>{displayText}</span>
          <ChevronDown size={compact ? 12 : 14} className="shrink-0 opacity-70" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-auto min-w-[180px] p-0 rounded-xl border border-gray-100 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] shadow-lg"
      >
        <div className="py-1">
          <button
            type="button"
            onClick={() => handleSelect(undefined)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-purple-700 dark:hover:text-purple-400 transition-colors ${!value ? "font-medium text-purple-600 dark:text-purple-400" : ""}`}
          >
            <span>{placeholder}</span>
            {!value && <Check size={16} className="shrink-0" strokeWidth={2.5} />}
          </button>
          {STUDY_LANGUAGE_CODES.map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => handleSelect(code)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-purple-700 dark:hover:text-purple-400 transition-colors ${value === code ? "font-medium text-purple-600 dark:text-purple-400" : ""}`}
            >
              <span>{LANG_NAMES[code]}</span>
              {value === code && <Check size={16} className="shrink-0" strokeWidth={2.5} />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export type ManualTabHandle = { handleSave: (navigateToStudy: boolean) => void };
type AutoImageJobStatus = "idle" | "queued" | "generating" | "retrying" | "success" | "no_image" | "failed";

const AUTO_IMAGE_BATCH_CONCURRENCY = 1;
const AUTO_IMAGE_BATCH_MAX_ATTEMPTS = 2;
const AUTO_IMAGE_BATCH_RETRY_DELAY_MS = 900;

interface SortableCardItemProps {
  id: string;
  card: Card;
  index: number;
  cellInputClass: string;
  t: (key: string) => string;
  isAutoSuggestEnabled: boolean;
  suggestions: { cardIndex: number; field: SuggestionField; options: string[] };
  showValidationErrors: boolean;
  generatingAICardIndices: Set<number>;
  autoImageStatuses: Record<number, AutoImageJobStatus | undefined>;
  uploadingCardIndex: number | null;
  handleTermChange: (i: number, v: string) => void;
  handleDefinitionChange: (i: number, v: string) => void;
  clearSuggestions: () => void;
  handleSelectSuggestion: (i: number, opt: string, field: SuggestionField) => void;
  updateCard: (i: number, field: keyof Card, value: unknown) => void;
  handleClearImage: (i: number) => void;
  handleGenerateAIImage: (i: number) => void;
  triggerFileUpload: (i: number) => void;
  setPreviewImage: (url: string | null) => void;
  removeCard: (i: number) => void;
}

function SortableCardItem({
  id,
  card,
  index: i,
  cellInputClass,
  t,
  isAutoSuggestEnabled,
  suggestions,
  showValidationErrors,
  generatingAICardIndices,
  autoImageStatuses,
  uploadingCardIndex,
  handleTermChange,
  handleDefinitionChange,
  clearSuggestions,
  handleSelectSuggestion,
  updateCard,
  handleClearImage,
  handleGenerateAIImage,
  triggerFileUpload,
  setPreviewImage,
  removeCard,
}: SortableCardItemProps) {
  const { setNodeRef, setActivatorNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const autoImageStatus = autoImageStatuses[i] ?? "idle";
  const isAutoImageBusy =
    generatingAICardIndices.has(i) ||
    autoImageStatus === "queued" ||
    autoImageStatus === "generating" ||
    autoImageStatus === "retrying";
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-card-index={i}
      className={`bg-white dark:bg-[hsl(var(--card-bg))] rounded-xl shadow-sm border border-gray-200 dark:border-[hsl(var(--border))] p-3 md:p-6 flex flex-col gap-2 md:gap-4 transition-all duration-200 scroll-mt-20 md:scroll-mt-24 ${isDragging ? "scale-[1.02] shadow-lg opacity-90 ring-2 ring-purple-400/50 z-10" : ""}`}
    >
      <div className="flex justify-between items-center border-b border-gray-200 dark:border-[hsl(var(--border))] pb-1.5">
        <span className="text-sm font-medium text-gray-500">{i + 1}</span>
        <div className="flex items-center gap-1">
          <div
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1 -m-1 touch-none select-none"
            aria-label={t("deckModal.dragToReorder")}
          >
            <GripHorizontal size={16} strokeWidth={2} />
          </div>
          <button type="button" onClick={() => removeCard(i)} className="p-1 rounded text-gray-400 hover:text-red-500"><Trash2 size={16} strokeWidth={2} /></button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 md:gap-4">
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t("deckModal.questionPlaceholder")}</label>
          <input
            value={card.front}
            onChange={(e) => handleTermChange(i, e.target.value)}
            onBlur={clearSuggestions}
            placeholder={t("deckModal.questionPlaceholder")}
            className={cellInputClass}
          />
          {isAutoSuggestEnabled && suggestions.cardIndex === i && suggestions.field === "front" && suggestions.options.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 w-full mt-1 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-lg shadow-lg overflow-hidden">
              {suggestions.options.map((opt, j) => (
                <button
                  key={j}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectSuggestion(i, opt, "front")}
                  className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm text-gray-800 dark:text-[hsl(var(--foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/10 border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-b-0 transition-colors"
                >
                  <Sparkles size={14} className="shrink-0 text-purple-500 dark:text-purple-400" />
                  {opt}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-col items-end">
            <LanguageSelector
              value={card.frontLang ?? ""}
              onChange={(code) => updateCard(i, "frontLang", code)}
              placeholder={t("deckModal.selectLanguagePlaceholder")}
            />
            {showValidationErrors && card.front.trim() && card.back.trim() && !(card.frontLang ?? "").trim() && (
              <span className="text-red-500 text-xs mt-1 block">{t("deckModal.selectTermLanguage")}</span>
            )}
          </div>
        </div>
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t("deckModal.answerPlaceholder")}</label>
          <input
            value={card.back}
            onChange={(e) => handleDefinitionChange(i, e.target.value)}
            onBlur={clearSuggestions}
            placeholder={t("deckModal.answerPlaceholder")}
            className={cellInputClass}
          />
          <div className="flex flex-col items-end">
            <LanguageSelector
              value={card.backLang ?? ""}
              onChange={(code) => updateCard(i, "backLang", code)}
              placeholder={t("deckModal.selectLanguagePlaceholder")}
            />
            {showValidationErrors && card.front.trim() && card.back.trim() && !(card.backLang ?? "").trim() && (
              <span className="text-red-500 text-xs mt-1 block">{t("deckModal.selectDefinitionLanguage")}</span>
            )}
          </div>
          {isAutoSuggestEnabled && suggestions.cardIndex === i && suggestions.field === "back" && suggestions.options.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 w-full mt-1 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-lg shadow-lg overflow-hidden">
              {suggestions.options.map((opt, j) => (
                <button
                  key={j}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectSuggestion(i, opt, "back")}
                  className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm text-gray-800 dark:text-[hsl(var(--foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/10 border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-b-0 transition-colors"
                >
                  <Sparkles size={14} className="shrink-0 text-purple-500 dark:text-purple-400" />
                  {opt}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 md:min-w-[140px] shrink-0">
          {card.imageUrl ? (
            <>
              <div className="w-full h-40 min-h-40 shrink-0 rounded-lg overflow-hidden border border-gray-200 dark:border-[hsl(var(--border))] cursor-pointer bg-[hsl(var(--muted))]/30 dark:bg-black/5 flex items-center justify-center" onClick={() => setPreviewImage(card.imageUrl!)}>
                <img src={card.imageUrl} alt="" className="w-full h-full object-contain shrink-0 rounded-md" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => handleClearImage(i)} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10">
                  <X size={14} />
                </button>
                <button type="button" onClick={() => handleGenerateAIImage(i)} disabled={isAutoImageBusy} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-[hsl(var(--primary))] hover:bg-purple-50 dark:hover:bg-purple-500/10 disabled:opacity-50">
                  {isAutoImageBusy ? <span className="spinner inline-block w-4 h-4 border-2 border-gray-300 border-t-[hsl(var(--primary))] rounded-full animate-spin" /> : <RefreshCw size={14} />}
                </button>
              </div>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => triggerFileUpload(i)}
            disabled={uploadingCardIndex === i}
            className="flex items-center justify-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 h-9 md:h-auto border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-purple-500 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-500 transition-colors font-medium text-xs disabled:opacity-50"
          >
            {uploadingCardIndex === i ? <span className="spinner inline-block w-3.5 h-3.5 border-2 rounded-full animate-spin" /> : <ImagePlus size={14} />}
            {t("deckModal.addPhoto")}
          </button>
          <button
            type="button"
            onClick={() => handleGenerateAIImage(i)}
            disabled={isAutoImageBusy}
            className="flex items-center justify-center gap-1.5 px-2.5 py-1.5 md:px-3 md:py-2 h-9 md:h-auto border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-purple-500 dark:hover:border-purple-500 hover:text-purple-600 dark:hover:text-purple-500 transition-colors font-medium text-xs disabled:opacity-50"
          >
            {isAutoImageBusy ? <span className="spinner inline-block w-3.5 h-3.5 border-2 rounded-full animate-spin" /> : <Sparkles size={14} />}
            {t("deckModal.aiImage")}
          </button>
          {(autoImageStatus === "queued" || autoImageStatus === "generating") && (
            <span className="text-[11px] text-gray-500 dark:text-gray-400 text-center">{t("deckModal.aiImagePending")}</span>
          )}
          {autoImageStatus === "retrying" && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400 text-center">{t("deckModal.aiImageRetrying")}</span>
          )}
          {autoImageStatus === "failed" && (
            <span className="text-[11px] text-red-500 dark:text-red-400 text-center">{t("deckModal.aiImageFailed")}</span>
          )}
          {autoImageStatus === "no_image" && (
            <span className="text-[11px] text-amber-600 dark:text-amber-400 text-center">{t("deckModal.aiImageNoImage")}</span>
          )}
        </div>
      </div>
      <div>
        <input value={card.hint || ""} onChange={(e) => updateCard(i, "hint", e.target.value)} placeholder={t("deckModal.hintPlaceholder")} className={cellInputClass} />
      </div>
    </div>
  );
}

type EditableModule = {
  id: string;
  title: string;
  subtitle?: string;
  cards: Card[];
  sourceLang?: string;
  targetLang?: string;
};

const ManualTab = forwardRef<ManualTabHandle, {
  deck?: Deck;
  initialType?: "words" | "cards" | "tests";
  onSave: (d: Omit<Deck, "id">, options?: { isFromAi?: boolean; navigateToStudy?: boolean }) => void;
  /** Called when saving multiple modules at once */
  onSaveMultiple?: (decks: Omit<Deck, "id">[], options?: { isFromAi?: boolean; navigateToStudy?: boolean }) => void;
  onClose: () => void;
  /** Cards injected from AI generation — when set, populates the manual list */
  prefilledCards?: Card[] | null;
  /** Multi-module prefill — when set, shows multi-module review UI */
  prefilledModules?: Array<{ title: string; subtitle?: string; cards: Card[]; sourceLang?: string; targetLang?: string }> | null;
  /** Lang/level from AI tab — preserved when switching to Manual; sourceLang/targetLang used for card language selectors */
  prefilledMeta?: {
    title?: string;
    lang?: string;
    level?: string;
    sourceLang?: string;
    targetLang?: string;
    sourceKind?: "ai" | "parsed";
  } | null;
  /** When true, auto-trigger Pollinations image generation for each new row */
  autoGenerateImagesForPrefilled?: boolean;
  prefillSignature?: string | null;
  /** Pollinations API keys for image generation (failover on 429) */
  pollinationsKeys?: string[];
  /** Quizlet-style full-width page layout */
  asPage?: boolean;
  /** Language validation: show inline errors when save attempted with missing front/back lang */
  showValidationErrors?: boolean;
  setShowValidationErrors?: (show: boolean) => void;
}>((props, ref) => {
  const { deck, initialType, onSave, onSaveMultiple, onClose, prefilledCards, prefilledModules, prefilledMeta, autoGenerateImagesForPrefilled, prefillSignature, pollinationsKeys = [], asPage, showValidationErrors: showValidationErrorsProp, setShowValidationErrors: setShowValidationErrorsProp } = props;
  const [localShowValidation, setLocalShowValidation] = useState(false);
  const showValidationErrors = showValidationErrorsProp ?? localShowValidation;
  const setShowValidationErrors = setShowValidationErrorsProp ?? setLocalShowValidation;
  const { t } = useTranslation();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingCardIndex, setUploadingCardIndex] = useState<number | null>(null);
  const [generatingAICardIndices, setGeneratingAICardIndices] = useState<Set<number>>(new Set());
  const [autoImageStatuses, setAutoImageStatuses] = useState<Record<number, AutoImageJobStatus | undefined>>({});
  const [autoImageBatchSummary, setAutoImageBatchSummary] = useState<{
    total: number;
    completed: number;
    failed: number;
    queued: number;
    generating: number;
    retrying: number;
    running: boolean;
  } | null>(null);
  const [autoImageStatusesForModules, setAutoImageStatusesForModules] = useState<Record<string, AutoImageJobStatus>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const defaultDeckType = deck?.deckType ?? (initialType ? INITIAL_TYPE_TO_DECK_TYPE[initialType] : "other");
  const defaultStudyModes: StudyModes = deck?.studyModes ?? { cards: true, learn: true, test: true, match: true, philology: true };
  const isMultiModuleMode = !deck && prefilledModules && prefilledModules.length > 0;
  const [title, setTitle] = useState(prefilledMeta?.title || deck?.title || "");
  const [description, setDescription] = useState(deck?.description || "");
  const [studyModes, setStudyModes] = useState<StudyModes>(defaultStudyModes);
  const [isTypeMenuOpen, setIsTypeMenuOpen] = useState(false);
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const [level, setLevel] = useState(() => {
    if (prefilledMeta?.level) return prefilledMeta.level;
    return deck?.level || "";
  });
  const [deckType, setDeckType] = useState(defaultDeckType);
  const [titleError, setTitleError] = useState("");
  const [cardsError, setCardsError] = useState("");
  const [editableModules, setEditableModules] = useState<EditableModule[]>(() => {
    if (isMultiModuleMode && prefilledModules) {
      return prefilledModules.map((m, i) => ({
        id: `mod_${i}_${Date.now()}`,
        title: m.title,
        subtitle: m.subtitle,
        cards: (m.cards ?? []).map((c) => ({
          ...c,
          frontLang: c.frontLang || m.sourceLang || "",
          backLang: c.backLang || m.targetLang || "",
        })),
        sourceLang: m.sourceLang,
        targetLang: m.targetLang,
      }));
    }
    return [];
  });
  const [expandedModuleIndices, setExpandedModuleIndices] = useState<Set<number>>(() => {
    if (isMultiModuleMode && prefilledModules && prefilledModules.length <= 5) {
      return new Set(prefilledModules.map((_, i) => i));
    }
    return new Set<number>();
  });
  const [cards, setCards] = useState<Card[]>(() => {
    if (isMultiModuleMode) return [{ front: "", back: "", frontLang: "", backLang: "", hint: "" }];
    if (prefilledCards && prefilledCards.length > 0) {
      const sourceLang = prefilledMeta?.sourceLang ?? "";
      const targetLang = prefilledMeta?.targetLang ?? "";
      return prefilledCards.map((c) => ({
        front: c.front,
        back: c.back,
        frontLang: (c.frontLang ?? "").trim() || sourceLang,
        backLang: (c.backLang ?? "").trim() || targetLang,
        hint: c.hint ?? "",
        imageUrl: c.imageUrl ?? null,
        imagePrompt: c.imagePrompt,
      }));
    }
    const fromDeck = deck?.cards?.map((c) => ({
      ...c,
      frontLang: c.frontLang ?? "",
      backLang: c.backLang ?? "",
    }));
    if (fromDeck && fromDeck.length > 0) return fromDeck;
    return [{ front: "", back: "", frontLang: "", backLang: "", hint: "" }];
  });
  const [applyLanguagesToAll, setApplyLanguagesToAll] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(() => {
    if (deck) return false;
    if (prefilledCards && prefilledCards.length > 0) return false;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw || raw.length < 3) return false;
      const draft = normalizeDraftFromStorage(raw);
      if (!draft) return false;
      return (
        draft.title !== "" ||
        draft.description !== "" ||
        draft.cards.some((c) => c.front !== "" || c.back !== "")
      );
    } catch {
      return false;
    }
  });
  const [showAiWarning, setShowAiWarning] = useState(
    () => !!(prefilledCards && prefilledCards.length > 0 && prefilledMeta?.sourceKind === "ai")
  );
  const [isAutoSuggestEnabled, setIsAutoSuggestEnabled] = useState(true);
  const [suggestions, setSuggestions] = useState<{ cardIndex: number; field: SuggestionField; options: string[] }>({
    cardIndex: -1,
    field: "front",
    options: [],
  });
  const suggestDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCards((prev) => {
      const oldIndex = prev.findIndex((_, i) => String(i) === active.id);
      const newIndex = prev.findIndex((_, i) => String(i) === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const handleTermChange = useCallback((cardIndex: number, value: string) => {
    updateCard(cardIndex, "front", value);
    if (!isAutoSuggestEnabled) return;
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    if (!value.trim() || value.trim().length < 2) {
      setSuggestions({ cardIndex: -1, field: "front", options: [] });
      return;
    }
    suggestDebounceRef.current = setTimeout(async () => {
      const opts = await fetchSuggestions(value, "front");
      setSuggestions({ cardIndex, field: "front", options: opts });
    }, SUGGEST_DEBOUNCE_MS);
  }, [isAutoSuggestEnabled]);

  const handleDefinitionChange = useCallback((cardIndex: number, value: string) => {
    updateCard(cardIndex, "back", value);
    if (!isAutoSuggestEnabled) return;
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
    if (!value.trim() || value.trim().length < 2) {
      setSuggestions({ cardIndex: -1, field: "back", options: [] });
      return;
    }
    suggestDebounceRef.current = setTimeout(async () => {
      const opts = await fetchSuggestions(value, "back");
      setSuggestions({ cardIndex, field: "back", options: opts });
    }, SUGGEST_DEBOUNCE_MS);
  }, [isAutoSuggestEnabled]);

  const handleSelectSuggestion = useCallback((cardIndex: number, option: string, field: SuggestionField) => {
    updateCard(cardIndex, field, option);
    setSuggestions({ cardIndex: -1, field: "front", options: [] });
  }, []);

  const clearSuggestions = useCallback(() => {
    setSuggestions((s) => (s.cardIndex >= 0 ? { cardIndex: -1, field: s.field, options: [] } : s));
  }, []);

  const addCard = () =>
    setCards((prev) => {
      const newCard: Card = { front: "", back: "", hint: "" };
      const last = prev[prev.length - 1];
      newCard.frontLang = last?.frontLang ?? "";
      newCard.backLang = last?.backLang ?? "";
      return [...prev, newCard];
    });
  const removeCard = (i: number) => setCards((prev) => prev.filter((_, idx) => idx !== i));
  const updateCard = (i: number, field: keyof Card, value: string | null | undefined) => {
    if (cardsError) setCardsError("");
    if (showValidationErrors && (field === "frontLang" || field === "backLang")) setShowValidationErrors(false);
    setCards((prev) => {
      let next = prev.map((c, idx) => (idx === i ? { ...c, [field]: value } : c));
      if (applyLanguagesToAll && i === 0 && (field === "frontLang" || field === "backLang")) {
        next = next.map((c, idx) => (idx === 0 ? c : { ...c, [field]: value }));
      }
      return next;
    });
  };

  const triggerFileUpload = (cardIndex: number) => {
    if (!user) {
      toast.error(t("deckModal.signInToUpload"));
      return;
    }
    (fileInputRef.current as HTMLInputElement & { _cardIndex?: number })._cardIndex = cardIndex;
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const cardIndex = (fileInputRef.current as HTMLInputElement & { _cardIndex?: number })?._cardIndex;
    e.target.value = "";
    if (!file || typeof cardIndex !== "number") return;
    if (!user) return;

    setUploadingCardIndex(cardIndex);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_").slice(0, 50);
      const path = `${user.id}/${Date.now()}_${safeName}`;

      const { error } = await supabase.storage
        .from("card_images")
        .upload(path, file, { upsert: false });

      if (error) throw error;

      const { data } = supabase.storage.from("card_images").getPublicUrl(path);
      updateCard(cardIndex, "imageUrl", data.publicUrl);
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error(t("deckModal.uploadError"));
    } finally {
      setUploadingCardIndex(null);
    }
  };

  const addGeneratingIndex = (i: number) => setGeneratingAICardIndices((prev) => new Set(prev).add(i));
  const removeGeneratingIndex = (i: number) => setGeneratingAICardIndices((prev) => {
    const n = new Set(prev);
    n.delete(i);
    return n;
  });
  const imagesFailedToastShown = useRef(false);
  const cardsRef = useRef(cards);
  const cardImageHistoryRef = useRef<Record<number, { imageUrls: Set<string>; providerAssetIds: Set<string> }>>({});
  const moduleCardImageHistoryRef = useRef<Record<string, { imageUrls: Set<string>; providerAssetIds: Set<string> }>>({});
  const autoImageQueueRef = useRef<number[]>([]);
  const autoImageAttemptCountsRef = useRef<Record<number, number>>({});
  const autoImageStatusesRef = useRef<Record<number, AutoImageJobStatus | undefined>>({});
  const autoImageBatchIndicesRef = useRef<number[]>([]);
  const autoImageBatchTokenRef = useRef(0);
  const autoImageBatchInitializedForRef = useRef<string | null>(null);
  const autoImageBatchRunningRef = useRef(false);
  const editableModulesRef = useRef<EditableModule[]>([]);
  const autoImageStatusesForModulesRef = useRef<Record<string, AutoImageJobStatus>>({});
  const autoImageQueueForModulesRef = useRef<string[]>([]);
  const autoImageBatchForModulesTokenRef = useRef(0);
  const autoImageBatchForModulesInitializedRef = useRef<string | null>(null);

  useEffect(() => {
    cardsRef.current = cards;
    cards.forEach((card, index) => {
      if (!card.imageUrl) return;
      if (!cardImageHistoryRef.current[index]) {
        cardImageHistoryRef.current[index] = { imageUrls: new Set<string>(), providerAssetIds: new Set<string>() };
      }
      cardImageHistoryRef.current[index].imageUrls.add(card.imageUrl);
    });
  }, [cards]);

  useEffect(() => {
    editableModulesRef.current = editableModules;
    editableModules.forEach((mod, moduleIndex) => {
      mod.cards.forEach((card, cardIndex) => {
        if (!card.imageUrl) return;
        const key = moduleCardKey(moduleIndex, cardIndex);
        if (!moduleCardImageHistoryRef.current[key]) {
          moduleCardImageHistoryRef.current[key] = { imageUrls: new Set<string>(), providerAssetIds: new Set<string>() };
        }
        moduleCardImageHistoryRef.current[key].imageUrls.add(card.imageUrl);
      });
    });
  }, [editableModules]);

  const rememberCardImageVariant = useCallback((index: number, imageUrl?: string | null, providerAssetId?: string) => {
    if (!imageUrl && !providerAssetId) return;
    if (!cardImageHistoryRef.current[index]) {
      cardImageHistoryRef.current[index] = { imageUrls: new Set<string>(), providerAssetIds: new Set<string>() };
    }
    if (imageUrl) {
      cardImageHistoryRef.current[index].imageUrls.add(imageUrl);
    }
    if (providerAssetId) {
      cardImageHistoryRef.current[index].providerAssetIds.add(providerAssetId);
    }
  }, []);

  const rememberModuleCardImageVariant = useCallback((key: string, imageUrl?: string | null, providerAssetId?: string) => {
    if (!imageUrl && !providerAssetId) return;
    if (!moduleCardImageHistoryRef.current[key]) {
      moduleCardImageHistoryRef.current[key] = { imageUrls: new Set<string>(), providerAssetIds: new Set<string>() };
    }
    if (imageUrl) {
      moduleCardImageHistoryRef.current[key].imageUrls.add(imageUrl);
    }
    if (providerAssetId) {
      moduleCardImageHistoryRef.current[key].providerAssetIds.add(providerAssetId);
    }
  }, []);

  const syncAutoImageBatchSummary = useCallback(() => {
    const indices = autoImageBatchIndicesRef.current;
    if (!indices.length) {
      setAutoImageBatchSummary(null);
      return;
    }
    let completed = 0;
    let failed = 0;
    let queued = 0;
    let generating = 0;
    let retrying = 0;
    for (const index of indices) {
      const status = autoImageStatusesRef.current[index];
      if (status === "success") completed += 1;
      else if (status === "failed" || status === "no_image") failed += 1;
      else if (status === "queued") queued += 1;
      else if (status === "generating") generating += 1;
      else if (status === "retrying") retrying += 1;
    }
    if (!autoImageBatchRunningRef.current && completed + failed >= indices.length && failed === 0) {
      setAutoImageBatchSummary(null);
      return;
    }
    setAutoImageBatchSummary({
      total: indices.length,
      completed,
      failed,
      queued,
      generating,
      retrying,
      running: autoImageBatchRunningRef.current,
    });
  }, []);

  const setAutoImageStatus = useCallback((index: number, status: AutoImageJobStatus) => {
    autoImageStatusesRef.current = {
      ...autoImageStatusesRef.current,
      [index]: status,
    };
    setAutoImageStatuses((prev) => {
      if (prev[index] === status) return prev;
      return { ...prev, [index]: status };
    });
    syncAutoImageBatchSummary();
  }, [syncAutoImageBatchSummary]);

  const handleGenerateAIImage = async (
    cardIndex: number,
    opts?: { isFromAutoGen?: boolean; mode?: "initial" | "regenerate" }
  ) => {
    const isFromAutoGen = opts?.isFromAutoGen ?? false;
    const card = cardsRef.current[cardIndex];
    const word = card?.front?.trim();
    if (!word) {
      if (!isFromAutoGen) {
        toast.error(t("deckModal.enterWordFirst"));
        return null;
      }
      throw new Error("MISSING_WORD");
    }
    if (!user) {
      if (!isFromAutoGen) {
        toast.error(t("deckModal.signInToUpload"));
        return null;
      }
      throw new Error("MISSING_USER");
    }

    const generationMode = opts?.mode ?? (card?.imageUrl ? "regenerate" : "initial");
    const deckTitle = title?.trim() || deck?.title;
    const promptWord = buildAiImagePrompt(word, card?.back, card?.imagePrompt, {
      mode: generationMode,
      deckTitle: deckTitle || undefined,
    });
    const imageHistory = cardImageHistoryRef.current[cardIndex];
    const excludeImageUrls =
      generationMode === "regenerate"
        ? Array.from(
            new Set([
              ...(imageHistory ? Array.from(imageHistory.imageUrls) : []),
              ...(card?.imageUrl ? [card.imageUrl] : []),
            ].filter(Boolean))
          )
        : undefined;
    const excludeProviderAssetIds =
      generationMode === "regenerate" && imageHistory?.providerAssetIds.size
        ? Array.from(imageHistory.providerAssetIds)
        : undefined;
    const variationSeed =
      generationMode === "regenerate"
        ? Date.now() + cardIndex + Math.floor(Math.random() * 1_000)
        : undefined;
    const logMode = opts?.mode ?? (card?.imageUrl ? "regenerate" : "initial");
    console.info("[DeckModal] deckmodal_image_generate_started", { cardIndex, mode: logMode });
    if (logMode === "regenerate") {
      console.info("[DeckModal] deckmodal_image_regenerate_started", { cardIndex });
    }
    setAutoImageStatus(cardIndex, "generating");
    addGeneratingIndex(cardIndex);
    try {
      const result = await requestAiImageUrl({
        prompt: promptWord,
        term: word,
        translationHint: card?.back?.trim() || undefined,
        sourceLanguage: card?.frontLang,
        targetLanguage: card?.backLang,
        mode: generationMode,
        excludeImageUrls,
        excludeProviderAssetIds,
        variationSeed,
        userId: user.id,
        pollinationsKeys,
      });
      if (result.kind === "no_image") {
        console.info("[DeckModal] deckmodal_image_no_image", { cardIndex });
        setAutoImageStatus(cardIndex, "no_image");
        return null;
      }
      if (result.kind === "no_new_image") {
        console.info("[DeckModal] deckmodal_image_regenerate_no_new_image", { cardIndex });
        setAutoImageStatus(cardIndex, "no_image");
        if (!isFromAutoGen) {
          toast.info(t("deckModal.aiImageNoNewImage"));
        }
        return null;
      }
      const { imageUrl, providerAssetId } = result;
      updateCard(cardIndex, "imageUrl", imageUrl);
      rememberCardImageVariant(cardIndex, imageUrl, providerAssetId);
      console.info("[DeckModal] deckmodal_image_replaced", { cardIndex, provider: result.provider });
      setAutoImageStatus(cardIndex, "success");
      if (generationMode === "regenerate") {
        console.info("[DeckModal] deckmodal_image_regenerate_succeeded", { cardIndex });
      }
      console.info("[DeckModal] deckmodal_image_generate_succeeded", { cardIndex });
      return imageUrl;
    } catch (err) {
      console.info("[DeckModal] deckmodal_image_error", { cardIndex, message: err instanceof Error ? err.message : String(err) });
      if (isFromAutoGen && !imagesFailedToastShown.current) {
        imagesFailedToastShown.current = true;
        toast.error(t("deckModal.wordsGeneratedImagesFailed"));
      } else if (!isFromAutoGen) {
        toast.error(t(getAiImageToastKey(err)));
        return null;
      }
      throw err;
    } finally {
      removeGeneratingIndex(cardIndex);
    }
  };

  const processAutoImageBatch = useCallback(async (batchToken: number) => {
    autoImageBatchRunningRef.current = true;
    syncAutoImageBatchSummary();
    const workers = Array.from({ length: AUTO_IMAGE_BATCH_CONCURRENCY }, async () => {
      while (autoImageBatchTokenRef.current === batchToken) {
        const nextIndex = autoImageQueueRef.current.shift();
        if (typeof nextIndex !== "number") return;
        const nextAttempt = (autoImageAttemptCountsRef.current[nextIndex] ?? 0) + 1;
        autoImageAttemptCountsRef.current[nextIndex] = nextAttempt;
        console.info("[auto-image-batch] card_generation_started", {
          batchToken,
          cardIndex: nextIndex,
          attempt: nextAttempt,
        });
        setAutoImageStatus(nextIndex, "generating");
        try {
          await handleGenerateAIImage(nextIndex, { isFromAutoGen: true });
          console.info("[auto-image-batch] card_generation_succeeded", {
            batchToken,
            cardIndex: nextIndex,
            attempt: nextAttempt,
          });
          setAutoImageStatus(nextIndex, "success");
        } catch (error) {
          const retryable = isRetryableAiImageError(error) && nextAttempt < AUTO_IMAGE_BATCH_MAX_ATTEMPTS;
          console.warn("[auto-image-batch] card_generation_failed", {
            batchToken,
            cardIndex: nextIndex,
            attempt: nextAttempt,
            retryable,
            error: error instanceof Error ? error.message : String(error),
          });
          if (retryable) {
            setAutoImageStatus(nextIndex, "retrying");
            await new Promise((resolve) => window.setTimeout(resolve, AUTO_IMAGE_BATCH_RETRY_DELAY_MS));
            if (autoImageBatchTokenRef.current !== batchToken) return;
            autoImageQueueRef.current.push(nextIndex);
            console.info("[auto-image-batch] batch_continued_after_failure", {
              batchToken,
              cardIndex: nextIndex,
              action: "requeued",
            });
            setAutoImageStatus(nextIndex, "queued");
          } else {
            setAutoImageStatus(nextIndex, "failed");
            console.info("[auto-image-batch] batch_continued_after_failure", {
              batchToken,
              cardIndex: nextIndex,
              action: "marked_failed",
            });
          }
        }
        await new Promise((resolve) => window.setTimeout(resolve, 160));
      }
    });
    await Promise.all(workers);
    if (autoImageBatchTokenRef.current !== batchToken) return;
    autoImageBatchRunningRef.current = false;
    syncAutoImageBatchSummary();
    const missingImages = autoImageBatchIndicesRef.current.filter((index) => {
      const card = cardsRef.current[index];
      return !card?.imageUrl;
    });
    console.info("[auto-image-batch] batch_completed", {
      batchToken,
      total: autoImageBatchIndicesRef.current.length,
      missingImages: missingImages.length,
    });
    if (missingImages.length > 0) {
      console.warn("[auto-image-batch] missing_images_remaining", {
        batchToken,
        cardIndices: missingImages,
      });
    }
  }, [handleGenerateAIImage, setAutoImageStatus, syncAutoImageBatchSummary]);

  const startAutoImageBatch = useCallback((indices: number[], reason: "initial" | "retry") => {
    autoImageBatchTokenRef.current += 1;
    const batchToken = autoImageBatchTokenRef.current;
    autoImageQueueRef.current = [...indices];
    autoImageBatchIndicesRef.current = Array.from(new Set([
      ...(reason === "retry" ? autoImageBatchIndicesRef.current : []),
      ...indices,
    ]));
    if (reason === "initial") {
      autoImageAttemptCountsRef.current = {};
      autoImageStatusesRef.current = {};
      const nextStatuses = indices.reduce<Record<number, AutoImageJobStatus>>((acc, index) => {
        acc[index] = "queued";
        return acc;
      }, {});
      autoImageStatusesRef.current = nextStatuses;
      setAutoImageStatuses(nextStatuses);
    } else {
      indices.forEach((index) => {
        autoImageStatusesRef.current = {
          ...autoImageStatusesRef.current,
          [index]: "queued",
        };
      });
      setAutoImageStatuses((prev) => {
        const next = { ...prev };
        indices.forEach((index) => {
          next[index] = "queued";
        });
        return next;
      });
    }
    autoImageBatchRunningRef.current = true;
    syncAutoImageBatchSummary();
    console.info("[auto-image-batch] batch_started", {
      batchToken,
      reason,
      total: indices.length,
    });
    indices.forEach((index) => {
      console.info("[auto-image-batch] card_enqueued", {
        batchToken,
        cardIndex: index,
      });
    });
    void processAutoImageBatch(batchToken);
  }, [processAutoImageBatch, syncAutoImageBatchSummary]);

  const retryMissingImages = useCallback(() => {
    const failedIndices = autoImageBatchIndicesRef.current.filter((index) => {
      const status = autoImageStatusesRef.current[index];
      const card = cardsRef.current[index];
      return (status === "failed" || status === "no_image") && card?.front?.trim() && !card?.imageUrl;
    });
    if (!failedIndices.length) return;
    startAutoImageBatch(failedIndices, "retry");
  }, [startAutoImageBatch]);

  useEffect(() => {
    const batchKey =
      autoGenerateImagesForPrefilled && prefilledCards?.length
        ? `${prefillSignature || "prefill"}:${prefilledCards.length}`
        : null;
    if (!batchKey || !user || autoImageBatchInitializedForRef.current === batchKey) return;
    autoImageBatchInitializedForRef.current = batchKey;
    imagesFailedToastShown.current = false;
    const indicesToGenerate = cardsRef.current
      .map((c, i) => (c.front?.trim() && !c.imageUrl ? i : -1))
      .filter((i) => i >= 0);
    if (indicesToGenerate.length === 0) {
      setAutoImageStatuses({});
      autoImageStatusesRef.current = {};
      autoImageBatchIndicesRef.current = [];
      setAutoImageBatchSummary(null);
      return;
    }
    startAutoImageBatch(indicesToGenerate, "initial");
  }, [autoGenerateImagesForPrefilled, user, prefilledCards?.length, prefillSignature, startAutoImageBatch]);

  useEffect(() => () => {
    autoImageBatchTokenRef.current += 1;
    autoImageBatchRunningRef.current = false;
  }, []);

  useEffect(() => {
    if (!previewImage) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewImage(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [previewImage]);

  useEffect(() => () => {
    if (suggestDebounceRef.current) clearTimeout(suggestDebounceRef.current);
  }, []);

  // Debounced autosave (2s): save to localStorage only after user stops typing. Same structure as Restore expects.
  const DRAFT_DEBOUNCE_MS = 2000;
  useEffect(() => {
    if (deck) return;
    const hasContent =
      (title && title.trim() !== "") ||
      (description && String(description).trim() !== "") ||
      cards.some((c) => (String(c?.front ?? "").trim() !== "" || String(c?.back ?? "").trim() !== ""));
    if (!hasContent) return;
    const timeoutId = setTimeout(() => {
      try {
        const payload = { title, description, cards };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [deck, title, description, cards]);

  useEffect(() => {
    if (!isTypeMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node)) {
        setIsTypeMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTypeMenuOpen]);

  const handleSelectAllModes = () => setStudyModes({ cards: true, learn: true, test: true, match: true, philology: true });

  const handleClearImage = useCallback((cardIndex: number) => {
    updateCard(cardIndex, "imageUrl", null);
    setAutoImageStatus(cardIndex, "idle");
    console.info("[DeckModal] deckmodal_image_removed", { cardIndex });
  }, [updateCard, setAutoImageStatus]);

  const titleInputRef = useRef<HTMLInputElement | null>(null);

  const handleSave = (navigateToStudy: boolean) => {
    setTitleError("");
    setCardsError("");
    setShowValidationErrors(false);

    if (isMultiModuleMode && editableModules.length > 0 && onSaveMultiple) {
      const validModules = editableModules
        .map((m) => ({
          ...m,
          validCards: m.cards.filter((c) => c.front.trim() && c.back.trim()),
        }))
        .filter((m) => m.title.trim() && m.validCards.length > 0);
      if (validModules.length === 0) {
        setCardsError(t("deckModal.addAtLeastOneCard"));
        toast.error(t("deckModal.addAtLeastOneCard"));
        return;
      }
      const saveType = (initialType ?? "words") as "words" | "cards" | "tests";
      const defaultLang = (prefilledMeta?.sourceLang || prefilledMeta?.targetLang || "en") as DeckLang;
      const decks: Omit<Deck, "id">[] = validModules.map((m) => ({
        title: m.title.trim(),
        lang: (m.sourceLang as DeckLang) || defaultLang,
        emoji: LANG_FLAGS[m.sourceLang || "en"] || "📚",
        cards: m.validCards,
        deckType: "vocab",
        type: saveType,
        studyModes: defaultStudyModes,
      }));
      onSaveMultiple(decks, { isFromAi: true, navigateToStudy });
      return;
    }

    if (!title.trim()) {
      setTitleError(t("deckModal.enterName"));
      toast.error(t("deckModal.pleaseEnterTitle", "Please enter the module title"));
      window.scrollTo({ top: 0, behavior: "smooth" });
      titleInputRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    const validCards = cards.filter((c) => c.front.trim() && c.back.trim());
    if (validCards.length === 0) {
      setCardsError(t("deckModal.addAtLeastOneCard"));
      toast.error(t("deckModal.addAtLeastOneCard"));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const firstInvalidIndex = cards.findIndex(
      (c) => c.front.trim() && c.back.trim() && (!(c.frontLang ?? "").trim() || !(c.backLang ?? "").trim())
    );
    if (firstInvalidIndex >= 0) {
      setShowValidationErrors(true);
      toast.error(t("deckModal.selectLanguagesForAll"));
      setTimeout(() => {
        const el = document.querySelector(`[data-card-index="${firstInvalidIndex}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      return;
    }
    const saveType = (initialType ?? deck?.type ?? "cards") as "words" | "cards" | "tests";
    onSave({
      title: title.trim(),
      description: description.trim() || undefined,
      lang: (deck?.lang ?? "en") as DeckLang,
      emoji: deck?.emoji ?? "📚",
      level,
      deckType,
      type: saveType,
      studyModes,
      cards: validCards,
    }, { navigateToStudy });
  };

  useImperativeHandle(ref, () => ({ handleSave }), [handleSave]);

  const cellInputClass = "w-full bg-gray-50 dark:bg-[hsl(var(--bg3))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl px-4 py-3 text-gray-800 dark:text-[hsl(var(--foreground))] placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-[hsl(var(--bg2))] focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all";

  const handleRestoreDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw || raw.length < 2) {
        toast.error(t("deckModal.draftNotFound", "Draft not found."));
        setShowDraftBanner(false);
        return;
      }
      const draft = normalizeDraftFromStorage(raw);
      if (!draft) {
        toast.error(t("deckModal.draftRestoreError", "Could not restore draft."));
        setShowDraftBanner(false);
        return;
      }
      const hasData =
        draft.title !== "" ||
        draft.description !== "" ||
        draft.cards.some((c) => c.front !== "" || c.back !== "");
      if (!hasData) {
        toast.error(t("deckModal.draftEmpty", "Draft is empty."));
        setShowDraftBanner(false);
        return;
      }
      // Physical state update: explicitly call setters with draft data so the form shows it.
      flushSync(() => {
        setTitle(draft.title);
        setDescription(draft.description);
        setCards(draft.cards.length > 0 ? draft.cards : [{ front: "", back: "", frontLang: "", backLang: "", hint: "" }]);
      });
      setShowDraftBanner(false);
    } catch {
      toast.error(t("deckModal.draftRestoreError", "Could not restore draft."));
      setShowDraftBanner(false);
    }
  };

  const handleDiscardDraft = () => {
    try {
      localStorage.removeItem(DRAFT_KEY);
      setTitle("");
      setDescription("");
      setCards([{ front: "", back: "", frontLang: "", backLang: "", hint: "" }]);
      setShowDraftBanner(false);
    } catch {
      setShowDraftBanner(false);
    }
  };

  const updateModuleTitle = (index: number, newTitle: string) => {
    setEditableModules((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], title: newTitle };
      return next;
    });
  };
  const updateModuleCard = (moduleIndex: number, cardIndex: number, field: keyof Card, value: string | undefined) => {
    setEditableModules((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      if (!mod) return prev;
      const newCards = [...mod.cards];
      if (newCards[cardIndex]) newCards[cardIndex] = { ...newCards[cardIndex], [field]: value ?? "" };
      next[moduleIndex] = { ...mod, cards: newCards };
      return next;
    });
  };
  const removeModule = (index: number) => {
    setEditableModules((prev) => prev.filter((_, i) => i !== index));
    setExpandedModuleIndices((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => { if (i < index) next.add(i); else if (i > index) next.add(i - 1); });
      return next;
    });
  };
  const toggleModuleExpand = (index: number) => {
    setExpandedModuleIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };
  const removeCardFromModule = (moduleIndex: number, cardIndex: number) => {
    setEditableModules((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      if (!mod) return prev;
      const newCards = mod.cards.filter((_, i) => i !== cardIndex);
      next[moduleIndex] = { ...mod, cards: newCards };
      return next;
    });
  };
  const addCardToModule = (moduleIndex: number) => {
    setEditableModules((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      if (!mod) return prev;
      const newCard: Card = {
        front: "",
        back: "",
        frontLang: mod.sourceLang ?? "",
        backLang: mod.targetLang ?? "",
        hint: "",
      };
      next[moduleIndex] = { ...mod, cards: [...mod.cards, newCard] };
      return next;
    });
    setExpandedModuleIndices((prev) => new Set(prev).add(moduleIndex));
  };

  const moduleCardKey = (moduleIndex: number, cardIndex: number) => `${moduleIndex}-${cardIndex}`;

  const setAutoImageStatusForModule = useCallback((key: string, status: AutoImageJobStatus) => {
    autoImageStatusesForModulesRef.current = { ...autoImageStatusesForModulesRef.current, [key]: status };
    setAutoImageStatusesForModules((prev) => (prev[key] === status ? prev : { ...prev, [key]: status }));
  }, []);

  const handleGenerateAIImageForModule = useCallback(
    async (
      moduleIndex: number,
      cardIndex: number,
      opts?: { isFromAutoGen?: boolean; mode?: "initial" | "regenerate" }
    ): Promise<string | null> => {
      const key = moduleCardKey(moduleIndex, cardIndex);
      const mods = editableModulesRef.current;
      const mod = mods[moduleIndex];
      const card = mod?.cards[cardIndex];
      const word = card?.front?.trim();
      if (!word) {
        if (!opts?.isFromAutoGen) toast.error(t("deckModal.enterWordFirst"));
        return null;
      }
      if (!user) {
        if (!opts?.isFromAutoGen) toast.error(t("deckModal.signInToUpload"));
        return null;
      }
      const generationMode = opts?.mode ?? (card?.imageUrl ? "regenerate" : "initial");
      const deckTitle = mod?.title?.trim();
      const promptWord = buildAiImagePrompt(word, card?.back, card?.imagePrompt, {
        mode: generationMode,
        deckTitle: deckTitle || undefined,
      });
      const imageHistory = moduleCardImageHistoryRef.current[key];
      const excludeImageUrls =
        generationMode === "regenerate"
          ? Array.from(
              new Set([
                ...(imageHistory ? Array.from(imageHistory.imageUrls) : []),
                ...(card?.imageUrl ? [card.imageUrl] : []),
              ].filter(Boolean))
            )
          : undefined;
      const excludeProviderAssetIds =
        generationMode === "regenerate" && imageHistory?.providerAssetIds.size
          ? Array.from(imageHistory.providerAssetIds)
          : undefined;
      setAutoImageStatusForModule(key, "generating");
      try {
        const result = await requestAiImageUrl({
          prompt: promptWord,
          term: word,
          translationHint: card?.back?.trim() || undefined,
          sourceLanguage: card?.frontLang,
          targetLanguage: card?.backLang,
          mode: generationMode,
          excludeImageUrls,
          excludeProviderAssetIds,
          variationSeed: generationMode === "regenerate" ? Date.now() + moduleIndex + cardIndex : undefined,
          userId: user.id,
          pollinationsKeys,
        });
        if (result.kind === "no_image") {
          console.info("[DeckModal] deckmodal_image_no_image", { moduleIndex, cardIndex });
          setAutoImageStatusForModule(key, "no_image");
          return null;
        }
        if (result.kind === "no_new_image") {
          console.info("[DeckModal] deckmodal_image_regenerate_no_new_image", { moduleIndex, cardIndex });
          setAutoImageStatusForModule(key, "no_image");
          if (!opts?.isFromAutoGen) toast.info(t("deckModal.aiImageNoNewImage"));
          return null;
        }
        const imageUrl = result.imageUrl;
        updateModuleCard(moduleIndex, cardIndex, "imageUrl", imageUrl ?? undefined);
        rememberModuleCardImageVariant(key, imageUrl, result.providerAssetId);
        setAutoImageStatusForModule(key, "success");
        return imageUrl ?? null;
      } catch (err) {
        console.error("[DeckModal] AI image generation failed (module card):", err);
        if (opts?.isFromAutoGen && !imagesFailedToastShown.current) {
          imagesFailedToastShown.current = true;
          toast.error(t("deckModal.wordsGeneratedImagesFailed"));
        } else if (!opts?.isFromAutoGen) {
          toast.error(t(getAiImageToastKey(err)));
        }
        setAutoImageStatusForModule(key, "failed");
        throw err;
      }
    },
    [user, pollinationsKeys, t, updateModuleCard, setAutoImageStatusForModule, rememberModuleCardImageVariant]
  );

  const processAutoImageBatchForModules = useCallback(async (batchToken: number) => {
    while (autoImageBatchForModulesTokenRef.current === batchToken) {
      const nextKey = autoImageQueueForModulesRef.current.shift();
      if (!nextKey) break;
      const [moduleIndexStr, cardIndexStr] = nextKey.split("-");
      const moduleIndex = parseInt(moduleIndexStr, 10);
      const cardIndex = parseInt(cardIndexStr, 10);
      if (Number.isNaN(moduleIndex) || Number.isNaN(cardIndex)) continue;
      setAutoImageStatusForModule(nextKey, "generating");
      try {
        await handleGenerateAIImageForModule(moduleIndex, cardIndex, { isFromAutoGen: true });
        setAutoImageStatusForModule(nextKey, "success");
      } catch (error) {
        const retryable = isRetryableAiImageError(error);
        if (retryable) {
          await new Promise((r) => window.setTimeout(r, AUTO_IMAGE_BATCH_RETRY_DELAY_MS));
          if (autoImageBatchForModulesTokenRef.current !== batchToken) return;
          autoImageQueueForModulesRef.current.push(nextKey);
          setAutoImageStatusForModule(nextKey, "queued");
        } else {
          setAutoImageStatusForModule(nextKey, "failed");
        }
      }
      await new Promise((r) => window.setTimeout(r, 160));
    }
  }, [handleGenerateAIImageForModule, setAutoImageStatusForModule]);

  const startAutoImageBatchForModules = useCallback(
    (entries: { moduleIndex: number; cardIndex: number }[]) => {
      if (!entries.length) return;
      autoImageBatchForModulesTokenRef.current += 1;
      const batchToken = autoImageBatchForModulesTokenRef.current;
      const keys = entries.map((e) => moduleCardKey(e.moduleIndex, e.cardIndex));
      autoImageQueueForModulesRef.current = [...keys];
      const nextStatuses: Record<string, AutoImageJobStatus> = {};
      keys.forEach((k) => {
        nextStatuses[k] = "queued";
        autoImageStatusesForModulesRef.current[k] = "queued";
      });
      setAutoImageStatusesForModules(nextStatuses);
      void processAutoImageBatchForModules(batchToken);
    },
    [processAutoImageBatchForModules]
  );

  useEffect(() => {
    if (!isMultiModuleMode || !autoGenerateImagesForPrefilled || !user || editableModules.length === 0) return;
    const modulesSignature = editableModules.map((m) => m.cards.length).join(",");
    const batchKey = `${prefillSignature ?? "prefill"}:modules:${editableModules.length}:${modulesSignature}`;
    if (autoImageBatchForModulesInitializedRef.current === batchKey) return;
    autoImageBatchForModulesInitializedRef.current = batchKey;
    imagesFailedToastShown.current = false;
    const entries: { moduleIndex: number; cardIndex: number }[] = [];
    editableModules.forEach((mod, midx) => {
      mod.cards.forEach((c, cidx) => {
        if (c.front?.trim() && !c.imageUrl) entries.push({ moduleIndex: midx, cardIndex: cidx });
      });
    });
    if (entries.length > 0) startAutoImageBatchForModules(entries);
  }, [isMultiModuleMode, autoGenerateImagesForPrefilled, user, prefillSignature, editableModules, startAutoImageBatchForModules]);

  const validModuleCount = editableModules.filter(
    (m) => m.title.trim() && m.cards.some((c) => c.front.trim() && c.back.trim())
  ).length;
  const totalCardCount = editableModules.reduce((s, m) => s + m.cards.length, 0);

  if (isMultiModuleMode) {
    return (
      <div className="flex flex-col min-h-0">
        {/* Top-level review summary */}
        <div className="mb-6 p-5 rounded-2xl bg-white/80 dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-[hsl(var(--border))] shadow-sm">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center">
              <Layers size={22} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-[hsl(var(--foreground))] mb-1">
                {t("deckModal.reviewSummaryTitle")}
              </h2>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[hsl(var(--muted-foreground))] mb-2">
                <span>{t("deckModal.reviewSummaryModules", { count: editableModules.length })}</span>
                <span>·</span>
                <span>{t("deckModal.reviewSummaryCards", { count: totalCardCount })}</span>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]/90">{t("deckModal.reviewSummaryHint")}</p>
            </div>
          </div>
        </div>

        {/* Module review cards */}
        {editableModules.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 rounded-2xl border-2 border-dashed border-gray-200 dark:border-[hsl(var(--border))] bg-gray-50/50 dark:bg-[hsl(var(--bg3))]/30">
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{t("deckModal.reviewNoModulesLeft")}</p>
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30"
            >
              {t("deckModal.backToHome")}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mb-8 pb-24 md:pb-8">
            {editableModules.map((mod, idx) => (
              <div
                key={mod.id}
                className="rounded-2xl border border-gray-200 dark:border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card-bg))] shadow-sm overflow-hidden transition-shadow hover:shadow-md"
              >
                {/* Module header */}
                <div className="px-4 md:px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 border-b border-gray-100 dark:border-[hsl(var(--border))]/50">
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={mod.title}
                      onChange={(e) => updateModuleTitle(idx, e.target.value)}
                      placeholder={t("deckModal.deckNamePlaceholder")}
                      className="w-full bg-transparent border-none text-base md:text-lg font-semibold focus:outline-none focus:ring-0 text-gray-900 dark:text-[hsl(var(--foreground))] placeholder-gray-400"
                      title={t("deckModal.renameModule")}
                    />
                    {mod.subtitle && (
                      <p className="text-xs md:text-sm text-[hsl(var(--muted-foreground))] mt-0.5">{mod.subtitle}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-[hsl(var(--bg3))]">
                      {t("deckModal.moduleCardCount", { count: mod.cards.length })}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleModuleExpand(idx)}
                      className="p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--foreground))] transition-colors"
                      title={expandedModuleIndices.has(idx) ? t("deckModal.collapseModule") : t("deckModal.expandModule")}
                    >
                      {expandedModuleIndices.has(idx) ? (
                        <ChevronUp size={18} strokeWidth={2.5} />
                      ) : (
                        <ChevronDown size={18} strokeWidth={2.5} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeModule(idx)}
                      className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      title={t("deckModal.removeModule")}
                    >
                      <Trash2 size={18} strokeWidth={2} />
                    </button>
                  </div>
                </div>

                {/* Cards list (expandable) */}
                {expandedModuleIndices.has(idx) && (
                  <div className="p-4 md:p-5 bg-gray-50/50 dark:bg-[hsl(var(--bg3))]/20">
                    <div className="space-y-3 max-h-80 md:max-h-96 overflow-y-auto pr-1">
                      {mod.cards.map((c, ci) => {
                        const moduleCardKeyStr = moduleCardKey(idx, ci);
                        const imageStatus = autoImageStatusesForModules[moduleCardKeyStr] ?? "idle";
                        const isGenerating = imageStatus === "generating" || imageStatus === "queued" || imageStatus === "retrying";
                        const showRegenerateOrRetry = Boolean(c.imageUrl) || isGenerating || imageStatus === "failed" || imageStatus === "no_image";
                        const imgBoxBase = "w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-xl shrink-0 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-[hsl(var(--border))] bg-[hsl(var(--muted))]/20 dark:bg-black/5";
                        return (
                          <div
                            key={ci}
                            className="flex flex-col gap-2 group p-3 rounded-xl bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-100 dark:border-[hsl(var(--border))]/50"
                          >
                            <div className="flex gap-2 sm:gap-3 items-start">
                              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                                <input
                                  value={c.front}
                                  onChange={(e) => updateModuleCard(idx, ci, "front", e.target.value)}
                                  placeholder={t("deckModal.questionPlaceholder")}
                                  className="flex-1 min-w-0 text-sm bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-[hsl(var(--border))] focus:border-purple-400 dark:focus:border-purple-500 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-purple-500/30 transition-colors"
                                />
                                <span className="hidden sm:inline text-gray-300 dark:text-gray-600 shrink-0">→</span>
                                <input
                                  value={c.back}
                                  onChange={(e) => updateModuleCard(idx, ci, "back", e.target.value)}
                                  placeholder={t("deckModal.answerPlaceholder")}
                                  className="flex-1 min-w-0 text-sm bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-[hsl(var(--border))] focus:border-purple-400 dark:focus:border-purple-500 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-purple-500/30 transition-colors text-[hsl(var(--accent2))] dark:text-emerald-400 font-medium"
                                />
                              </div>
                              <div className="flex items-center gap-2 shrink-0 min-w-0">
                                <div className={imgBoxBase}>
                                  {c.imageUrl ? (
                                    <button
                                      type="button"
                                      onClick={() => setPreviewImage(c.imageUrl ?? null)}
                                      className="w-full h-full rounded-xl overflow-hidden cursor-pointer flex items-center justify-center"
                                    >
                                      <img src={c.imageUrl} alt="" className="w-full h-full object-contain rounded-xl" />
                                    </button>
                                  ) : isGenerating ? (
                                    <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} title={imageStatus === "retrying" ? t("deckModal.aiImageRetrying") : t("deckModal.aiImagePending")} />
                                  ) : imageStatus === "failed" ? (
                                    <span className="text-[10px] font-medium text-red-500 dark:text-red-400 px-1.5 text-center leading-tight">{t("deckModal.aiImageFailed")}</span>
                                  ) : c.front?.trim() ? (
                                    <button
                                      type="button"
                                      onClick={() => handleGenerateAIImageForModule(idx, ci)}
                                      className="w-full h-full flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/10 hover:text-[hsl(var(--primary))] rounded-xl transition-colors"
                                      title={t("deckModal.generateAIImage")}
                                    >
                                      <ImagePlus size={18} strokeWidth={2} />
                                    </button>
                                  ) : (
                                    <span className="w-full h-full bg-gray-50/50 dark:bg-[hsl(var(--bg3))]/30 rounded-xl" />
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {c.imageUrl || (c.front?.trim() && showRegenerateOrRetry) ? (
                                    <button
                                      type="button"
                                      onClick={() => handleGenerateAIImageForModule(idx, ci, { mode: c.imageUrl ? "regenerate" : undefined })}
                                      disabled={isGenerating}
                                      className="p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:bg-gray-100 dark:hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--primary))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                      title={t("deckModal.regenerateImage")}
                                    >
                                      <RefreshCw size={16} strokeWidth={2} />
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => removeCardFromModule(idx, ci)}
                                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 opacity-70 hover:opacity-100 transition-opacity"
                                    title={t("deckModal.removeCard")}
                                  >
                                    <X size={16} strokeWidth={2.5} />
                                  </button>
                                </div>
                              </div>
                            </div>
                            {(imageStatus === "queued" || imageStatus === "generating") && (
                              <span className="text-[11px] text-gray-500 dark:text-gray-400">{t("deckModal.aiImagePending")}</span>
                            )}
                            {imageStatus === "retrying" && (
                              <span className="text-[11px] text-amber-600 dark:text-amber-400">{t("deckModal.aiImageRetrying")}</span>
                            )}
                            {imageStatus === "failed" && c.front?.trim() && (
                              <span className="text-[11px] text-red-500 dark:text-red-400">{t("deckModal.aiImageFailed")} · {t("deckModal.regenerateImage")}</span>
                            )}
                            {imageStatus === "no_image" && c.front?.trim() && (
                              <span className="text-[11px] text-amber-600 dark:text-amber-400">{t("deckModal.aiImageNoImage")}</span>
                            )}
                            <input
                              value={c.hint ?? ""}
                              onChange={(e) => updateModuleCard(idx, ci, "hint", e.target.value)}
                              placeholder={t("deckModal.hintPlaceholder")}
                              className="text-xs bg-gray-50/80 dark:bg-[hsl(var(--bg3))]/50 border border-transparent hover:border-gray-200 dark:hover:border-[hsl(var(--border))] rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500/30 text-[hsl(var(--muted-foreground))] placeholder-gray-400 w-full max-w-xs"
                            />
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => addCardToModule(idx)}
                      className="mt-3 w-full py-2.5 rounded-xl border-2 border-dashed border-gray-200 dark:border-[hsl(var(--border))] text-sm font-medium text-[hsl(var(--muted-foreground))] hover:border-purple-400 hover:text-purple-600 dark:hover:text-purple-400 dark:hover:border-purple-500/50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus size={18} strokeWidth={2.5} />
                      {t("deckModal.addCardToModule")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Sticky CTA bar (mobile) / Fixed bottom (desktop) */}
        {editableModules.length > 0 && (
          <div className="fixed bottom-0 left-0 right-0 md:relative md:mt-auto p-4 md:p-0 bg-white/95 dark:bg-[hsl(var(--bg2))]/95 backdrop-blur-sm border-t border-gray-200 dark:border-[hsl(var(--border))] md:border-0 md:bg-transparent md:backdrop-blur-none z-10">
            <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-3 md:gap-4">
              <button
                type="button"
                onClick={() => handleSave(false)}
                className="order-2 sm:order-1 flex-1 px-6 py-3.5 rounded-xl font-bold bg-purple-600 text-white hover:bg-purple-700 active:scale-[0.99] transition-all shadow-lg shadow-purple-500/25"
              >
                {t("deckModal.createAllModules", { count: validModuleCount })}
              </button>
              <button
                type="button"
                onClick={() => handleSave(true)}
                className="order-1 sm:order-2 flex-1 px-6 py-3.5 rounded-xl font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors"
              >
                {t("deckModal.createAndStudy")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="order-3 px-6 py-3.5 rounded-xl font-medium border border-gray-200 dark:border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-gray-50 dark:hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--foreground))] transition-colors"
              >
                {t("deckModal.cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (asPage) {
    return (
      <>
        {!deck && showDraftBanner && (
          <div className="fixed left-4 right-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-50 bottom-24 md:bottom-8 flex justify-center animate-in slide-in-from-bottom-5 fade-in duration-300">
            <div className="relative w-max max-w-full rounded-2xl shadow-xl border border-border/50 bg-background/95 backdrop-blur px-4 py-3.5 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleRestoreDraft}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors shadow-sm border-0"
              >
                <History className="w-4 h-4 shrink-0" />
                {t("deckModal.restoreDraft")}
              </button>
              <button
                type="button"
                onClick={handleDiscardDraft}
                className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl bg-slate-200/80 dark:bg-slate-700/80 text-slate-700 dark:text-slate-200 hover:bg-slate-300/80 dark:hover:bg-slate-600/80 text-sm font-semibold transition-colors border border-slate-300/50 dark:border-slate-600/50"
              >
                <Trash2 className="w-4 h-4 shrink-0" />
                {t("deckModal.startNew")}
              </button>
              <button
                type="button"
                onClick={() => setShowDraftBanner(false)}
                className="absolute -top-1.5 -right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 border border-border text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        {/* Title & Description cells */}
        <div className="flex flex-col md:flex-row gap-4 mb-2 md:mb-8 w-full">
          <input
            ref={titleInputRef}
            type="text"
            value={title}
            onChange={(e) => { setTitle(e.target.value); if (titleError) setTitleError(""); }}
            placeholder={t("deckModal.deckNamePlaceholder")}
            className={`flex-1 bg-gray-50 dark:bg-[hsl(var(--bg3))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl px-4 py-3.5 md:py-4 text-lg md:text-xl font-semibold text-gray-800 dark:text-[hsl(var(--foreground))] placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-[hsl(var(--bg2))] focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all ${titleError ? "border-red-500 dark:border-red-500" : ""}`}
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("deckModal.descriptionPlaceholder")}
            rows={2}
            className="flex-1 min-h-[60px] resize-none bg-gray-50 dark:bg-[hsl(var(--bg3))] border border-gray-200 dark:border-[hsl(var(--border))] rounded-xl px-4 py-3 text-sm md:text-base font-normal text-gray-800 dark:text-[hsl(var(--foreground))] placeholder-gray-400 dark:placeholder-gray-500 focus:bg-white dark:focus:bg-[hsl(var(--bg2))] focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
          />
        </div>
        {titleError && <p className="text-red-500 text-sm mb-4">{titleError}</p>}

        {/* Settings bar */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 md:gap-6 mb-2 md:mb-8 p-3 md:p-4 rounded-xl bg-gray-50 dark:bg-[hsl(var(--bg3))]/50">
          <div>
            <label className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t("deckModal.level")}</label>
            <select value={level} onChange={(e) => setLevel(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg bg-white dark:bg-[hsl(var(--bg3))] border border-gray-200 dark:border-[hsl(var(--border))] text-sm">
              <option value="">{t("deckModal.levelGeneral")}</option>
              {["A1","A2","B1","B2","C1","C2"].map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div ref={typeMenuRef} className="relative">
            <label className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t("deckModal.type")}</label>
            <button
              type="button"
              onClick={() => setIsTypeMenuOpen((o) => !o)}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-white dark:bg-[hsl(var(--bg3))] border border-gray-200 dark:border-[hsl(var(--border))] text-sm text-left flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <span className="text-gray-800 dark:text-[hsl(var(--foreground))]">
                {t("deckModal.selectedCount", { count: [studyModes.cards, studyModes.learn, studyModes.test, studyModes.match, studyModes.philology].filter(Boolean).length })}
              </span>
              <ChevronDown size={16} className={`text-gray-500 shrink-0 transition-transform ${isTypeMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {isTypeMenuOpen && (
              <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-[hsl(var(--card-bg))] rounded-xl shadow-lg border border-gray-200 dark:border-[hsl(var(--border))] p-4 z-20 w-full min-w-[200px]">
                <button
                  type="button"
                  onClick={handleSelectAllModes}
                  className="text-xs text-purple-600 dark:text-purple-400 font-semibold mb-3 hover:underline"
                >
                  {t("deckModal.selectAll")}
                </button>
                <div className="space-y-2">
                  {[
                    { key: "cards" as const, label: t("deckModal.studyModeCards") },
                    { key: "learn" as const, label: t("deckModal.studyModeLearn") },
                    { key: "test" as const, label: t("deckModal.studyModeTest") },
                    { key: "match" as const, label: t("deckModal.studyModeMatch") },
                    { key: "philology" as const, label: t("deckModal.studyModePhilology") },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-800 dark:text-[hsl(var(--foreground))]">{label}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={studyModes[key]}
                        onClick={() => setStudyModes((s) => ({ ...s, [key]: !s[key] }))}
                        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                          studyModes[key] ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-600"
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            studyModes[key] ? "translate-x-4" : "translate-x-0.5"
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
        {cardsError && <p className="text-red-500 text-sm mb-4">{cardsError}</p>}

        {/* Import + Apply languages + Suggestions — above cards */}
        <div className="flex flex-wrap justify-between items-center gap-3 md:gap-4 mb-3 md:mb-4">
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-purple-200 dark:border-purple-700 bg-white dark:bg-[hsl(var(--card-bg))] text-purple-600 dark:text-purple-400 font-medium text-sm hover:bg-purple-100 dark:hover:bg-purple-500/10 hover:border-purple-300 dark:hover:border-purple-600 transition-all"
          >
            <Plus size={16} />
            {t("deckModal.import")}
          </button>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("deckModal.applyLanguagesToAll")}</span>
              <button
                type="button"
                role="switch"
                aria-checked={applyLanguagesToAll}
                onClick={() => setApplyLanguagesToAll((x) => !x)}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                  applyLanguagesToAll ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-600"
                }`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${applyLanguagesToAll ? "translate-x-4" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{t("deckModal.suggestionsLabel")}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 cursor-help"><Info size={14} className="text-gray-400 dark:text-gray-500" /></span>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="max-w-xs bg-white text-gray-700 border border-gray-200 shadow-lg rounded-lg p-3 text-sm z-50 transition-all duration-200 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
                >
                  {t("deckModal.suggestionsTooltip")}
                </TooltipContent>
              </Tooltip>
              <button
                type="button"
                role="switch"
                aria-checked={isAutoSuggestEnabled}
                onClick={() => setIsAutoSuggestEnabled((x) => !x)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  isAutoSuggestEnabled ? "bg-purple-500" : "bg-gray-200 dark:bg-gray-600"
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                    isAutoSuggestEnabled ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* AI review warning */}
        {showAiWarning && (
          <div className="relative bg-amber-50/80 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-4 mb-5 flex items-center gap-3 animate-in fade-in duration-300">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
            <span className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              {t("deckModal.aiReviewWarning")}
            </span>
            <button
              type="button"
              onClick={() => setShowAiWarning(false)}
              className="ml-auto shrink-0 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Top action bar — duplicate for mobile so user doesn't have to scroll to bottom */}
        <div className="flex flex-wrap gap-2 mb-4 md:mb-5">
          <button
            type="button"
            onClick={() => handleSave(false)}
            className="flex-1 min-w-[140px] px-4 py-3 rounded-xl font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors text-sm md:text-base"
          >
            {deck ? t("deckModal.save") : t("deckModal.create")}
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            className="flex-1 min-w-[140px] px-4 py-3 rounded-xl font-bold bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-md text-sm md:text-base"
          >
            {deck ? t("deckModal.saveAndStudy") : t("deckModal.createAndStudy")}
          </button>
        </div>

        {autoImageBatchSummary && (autoImageBatchSummary.running || autoImageBatchSummary.failed > 0) && (
          <div className="mb-4 md:mb-5 rounded-xl border border-purple-200 dark:border-purple-500/30 bg-purple-50/80 dark:bg-purple-500/10 px-4 py-3 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
              {autoImageBatchSummary.running
                ? t("deckModal.autoImageBatchProcessing", {
                    done: autoImageBatchSummary.completed + autoImageBatchSummary.failed,
                    total: autoImageBatchSummary.total,
                  })
                : t("deckModal.autoImageBatchFailed", {
                    failed: autoImageBatchSummary.failed,
                  })}
            </span>
            {autoImageBatchSummary.failed > 0 && (
              <button
                type="button"
                onClick={retryMissingImages}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-purple-700 dark:text-purple-200 hover:bg-purple-100 dark:hover:bg-purple-500/20 transition-colors"
              >
                <RotateCcw size={14} />
                {t("deckModal.retryMissingImages")}
              </button>
            )}
          </div>
        )}

        {/* Cards list — Quizlet-style blocks */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={cards.map((_, i) => String(i))} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
          {cards.map((card, i) => (
            <SortableCardItem
              key={i}
              id={String(i)}
              card={card}
              index={i}
              cellInputClass={cellInputClass}
              t={t}
              isAutoSuggestEnabled={isAutoSuggestEnabled}
              suggestions={suggestions}
              showValidationErrors={showValidationErrors}
              generatingAICardIndices={generatingAICardIndices}
              autoImageStatuses={autoImageStatuses}
              uploadingCardIndex={uploadingCardIndex}
              handleTermChange={handleTermChange}
              handleDefinitionChange={handleDefinitionChange}
              clearSuggestions={clearSuggestions}
              handleSelectSuggestion={handleSelectSuggestion}
              updateCard={updateCard}
              handleClearImage={handleClearImage}
              handleGenerateAIImage={handleGenerateAIImage}
              triggerFileUpload={triggerFileUpload}
              setPreviewImage={setPreviewImage}
              removeCard={removeCard}
            />
          ))}
        </div>
          </SortableContext>
        </DndContext>

        {/* Add card block — green styling to indicate creation */}
        <button
          type="button"
          onClick={() => { addCard(); if (cardsError) setCardsError(""); }}
          className="w-full py-5 md:py-8 bg-white dark:bg-[hsl(var(--card-bg))] border-2 border-dashed border-green-600 md:border-green-500 rounded-xl flex items-center justify-center font-bold text-green-700 dark:text-green-500 hover:bg-green-50 active:bg-green-100 dark:hover:bg-green-950/30 transition-colors cursor-pointer text-sm md:text-base"
        >
          {t("deckModal.addCardBlock")}
        </button>

        {/* Bottom action bar — symmetric 2-col grid on mobile, inline on desktop */}
        <div className="grid grid-cols-2 gap-2 w-full md:flex md:justify-end md:items-center md:gap-4 mt-6 md:mt-8 mb-8 md:mb-12">
          <button
            type="button"
            onClick={() => handleSave(false)}
            className="w-full min-w-0 px-4 md:px-8 py-3 rounded-xl font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors text-sm md:text-base"
          >
            {deck ? t("deckModal.save") : t("deckModal.create")}
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            className="w-full min-w-0 px-4 md:px-8 py-3 rounded-xl font-bold bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-md text-sm md:text-base"
          >
            {deck ? t("deckModal.saveAndStudy") : t("deckModal.createAndStudy")}
          </button>
        </div>

        {previewImage && createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setPreviewImage(null)} role="dialog" aria-modal="true">
            <button type="button" onClick={() => setPreviewImage(null)} className="absolute top-4 right-4 z-[101] w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"><X size={24} /></button>
            <img src={previewImage} alt="Preview" className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>,
          document.body
        )}
        <ImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={(imported) => {
            setCards((prev) => {
              const validExisting = prev.filter((c) => (c.front || "").trim() !== "" || (c.back || "").trim() !== "");
              const sourceLang = prev[0] ?? validExisting[0] ?? prev[prev.length - 1];
              const inheritedFrontLang = sourceLang?.frontLang ?? "";
              const inheritedBackLang = sourceLang?.backLang ?? "";
              return [
                ...validExisting,
                ...imported.map((c) => ({
                  ...c,
                  frontLang: (c.frontLang ?? "").trim() || inheritedFrontLang,
                  backLang: (c.backLang ?? "").trim() || inheritedBackLang,
                })),
              ];
            });
            toast.success(`${imported.length} ${t("deckModal.cardsImported")}`);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Field label={t("deckModal.deckName")}>
        <input
          ref={titleInputRef}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (titleError) setTitleError("");
          }}
          placeholder={t("deckModal.deckNamePlaceholder")}
          style={{
            ...inputStyle,
            borderColor: titleError ? "rgb(239, 68, 68)" : undefined,
          }}
        />
        {titleError && (
          <p className="text-red-500 text-xs mt-1" style={{ color: "rgb(239, 68, 68)", marginTop: 4, fontSize: 12 }}>
            {titleError}
          </p>
        )}
      </Field>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Field label={t("deckModal.level")} style={{ flex: 1 }}>
          <select value={level} onChange={(e) => setLevel(e.target.value)} style={inputStyle}>
            <option value="">{t("deckModal.levelGeneral")}</option>
            {["A1","A2","B1","B2","C1","C2"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </Field>
        <Field label={t("deckModal.type")} style={{ flex: 1 }}>
          <div ref={typeMenuRef} style={{ position: "relative" }}>
            <button
            type="button"
            onClick={() => setIsTypeMenuOpen((o) => !o)}
            style={{ ...inputStyle, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, cursor: "pointer", textAlign: "left" }}
          >
            <span>{t("deckModal.selectedCount", { count: [studyModes.cards, studyModes.learn, studyModes.test, studyModes.match, studyModes.philology].filter(Boolean).length })}</span>
            <ChevronDown size={16} style={{ transform: isTypeMenuOpen ? "rotate(180deg)" : "none", flexShrink: 0 }} />
          </button>
          {isTypeMenuOpen && (
            <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-[hsl(var(--card-bg))] rounded-xl shadow-lg border border-gray-200 dark:border-[hsl(var(--border))] p-4 z-20 min-w-[200px]">
              <button type="button" onClick={handleSelectAllModes} className="text-xs text-purple-600 dark:text-purple-400 font-semibold mb-3 hover:underline">
                {t("deckModal.selectAll")}
              </button>
              <div className="space-y-2">
                {[
                  { key: "cards" as const, label: t("deckModal.studyModeCards") },
                  { key: "learn" as const, label: t("deckModal.studyModeLearn") },
                  { key: "test" as const, label: t("deckModal.studyModeTest") },
                  { key: "match" as const, label: t("deckModal.studyModeMatch") },
                  { key: "philology" as const, label: t("deckModal.studyModePhilology") },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between gap-2">
                    <span className="text-sm text-gray-800 dark:text-[hsl(var(--foreground))]">{label}</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={studyModes[key]}
                      onClick={() => setStudyModes((s) => ({ ...s, [key]: !s[key] }))}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                        studyModes[key] ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-600"
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${studyModes[key] ? "translate-x-4" : "translate-x-0.5"}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </Field>
      </div>

      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div className="flex items-center gap-3">
          <label style={labelStyle}>{t("deckModal.cards")}</label>
          <button
            type="button"
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-purple-200 dark:border-purple-700 bg-white dark:bg-[hsl(var(--card-bg))] text-purple-600 dark:text-purple-400 font-medium text-sm hover:bg-purple-100 dark:hover:bg-purple-500/10 hover:border-purple-300 dark:hover:border-purple-600 transition-all"
          >
            <Plus size={16} />
            {t("deckModal.import")}
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t("deckModal.applyLanguagesToAll")}</span>
            <button
              type="button"
              role="switch"
              aria-checked={applyLanguagesToAll}
              onClick={() => setApplyLanguagesToAll((x) => !x)}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 ${
                applyLanguagesToAll ? "bg-purple-600" : "bg-gray-200 dark:bg-gray-600"
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${applyLanguagesToAll ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t("deckModal.suggestionsLabel")}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-help"><Info size={14} className="text-gray-400" /></span>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-xs bg-white text-gray-700 border border-gray-200 shadow-lg rounded-lg p-3 text-sm z-50 transition-all duration-200 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
              >
                {t("deckModal.suggestionsTooltip")}
              </TooltipContent>
            </Tooltip>
            <button
              type="button"
              role="switch"
              aria-checked={isAutoSuggestEnabled}
              onClick={() => setIsAutoSuggestEnabled((x) => !x)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                isAutoSuggestEnabled ? "bg-purple-500" : "bg-gray-200 dark:bg-gray-600"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                  isAutoSuggestEnabled ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>
          <button
            onClick={() => {
              addCard();
              if (cardsError) setCardsError("");
            }}
            style={ghostBtnSm}
          >
            {t("deckModal.addCard")}
          </button>
        </div>
      </div>
      {cardsError && (
        <p style={{ color: "rgb(239, 68, 68)", fontSize: 12, marginTop: -4, marginBottom: 8 }}>{cardsError}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileSelect}
      />
      <div className="flex flex-col gap-4 w-full min-w-0 overflow-y-auto" style={{ maxHeight: 320, marginBottom: 16 }}>
        {cards.map((card, i) => (
          <div key={i} data-card-index={i} className="flex flex-wrap gap-2 sm:gap-4 items-start w-full min-w-0 scroll-mt-16">
            <div style={{ position: "relative", flex: 1, minWidth: 100 }}>
              <input
                value={card.front}
                onChange={(e) => handleTermChange(i, e.target.value)}
                onBlur={clearSuggestions}
                placeholder={t("deckModal.questionPlaceholder")}
                style={{ ...cardInputStyle, width: "100%" }}
              />
              {isAutoSuggestEnabled && suggestions.cardIndex === i && suggestions.field === "front" && suggestions.options.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 w-full mt-1 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {suggestions.options.map((opt, j) => (
                    <button
                      key={j}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectSuggestion(i, opt, "front")}
                      className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm text-gray-800 dark:text-[hsl(var(--foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/10 border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-b-0 transition-colors"
                    >
                      <Sparkles size={14} className="shrink-0 text-purple-500 dark:text-purple-400" />
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-col items-end">
                <LanguageSelector
                  value={card.frontLang ?? ""}
                  onChange={(code) => updateCard(i, "frontLang", code)}
                  placeholder={t("deckModal.selectLanguagePlaceholder")}
                  compact
                />
                {showValidationErrors && card.front.trim() && card.back.trim() && !(card.frontLang ?? "").trim() && (
                  <span className="text-red-500 text-xs mt-1 block">{t("deckModal.selectTermLanguage")}</span>
                )}
              </div>
            </div>
            <div style={{ position: "relative", flex: 1, minWidth: 100 }}>
              <input
                value={card.back}
                onChange={(e) => handleDefinitionChange(i, e.target.value)}
                onBlur={clearSuggestions}
                placeholder={t("deckModal.answerPlaceholder")}
                style={{ ...cardInputStyle, width: "100%" }}
              />
              <div className="flex flex-col items-end">
                <LanguageSelector
                  value={card.backLang ?? ""}
                  onChange={(code) => updateCard(i, "backLang", code)}
                  placeholder={t("deckModal.selectLanguagePlaceholder")}
                  compact
                />
                {showValidationErrors && card.front.trim() && card.back.trim() && !(card.backLang ?? "").trim() && (
                  <span className="text-red-500 text-xs mt-1 block">{t("deckModal.selectDefinitionLanguage")}</span>
                )}
              </div>
              {isAutoSuggestEnabled && suggestions.cardIndex === i && suggestions.field === "back" && suggestions.options.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-50 w-full mt-1 bg-white dark:bg-[hsl(var(--card-bg))] border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  {suggestions.options.map((opt, j) => (
                    <button
                      key={j}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleSelectSuggestion(i, opt, "back")}
                      className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-sm text-gray-800 dark:text-[hsl(var(--foreground))] hover:bg-purple-50 dark:hover:bg-purple-500/10 border-b border-gray-100 dark:border-[hsl(var(--border))] last:border-b-0 transition-colors"
                    >
                      <Sparkles size={14} className="shrink-0 text-purple-500 dark:text-purple-400" />
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <input value={card.hint || ""} onChange={(e) => updateCard(i, "hint", e.target.value)} placeholder={t("deckModal.hintPlaceholder")} style={{ ...cardInputStyle, maxWidth: 80 }} />
            {/* Image: prominent dashed buttons */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0, minWidth: 120 }}>
              {card.imageUrl && (
                <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                  <div style={{ width: 48, height: 48, minWidth: 48, minHeight: 48, borderRadius: "var(--radius-sm)", border: "1px solid hsl(var(--border))", overflow: "hidden", flexShrink: 0, cursor: "pointer", background: "hsl(var(--muted) / 0.3)" }} onClick={() => setPreviewImage(card.imageUrl!)}>
                    <img src={card.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", flexShrink: 0 }} />
                  </div>
                  <button type="button" onClick={() => handleClearImage(i)} style={{ ...iconBtnStyle, padding: 4 }} title={t("deckModal.clearImage")}><X size={14} /></button>
                  <button type="button" onClick={() => handleGenerateAIImage(i)} disabled={generatingAICardIndices.has(i)} style={iconBtnStyle} title={t("deckModal.regenerateImage")}>
                    {generatingAICardIndices.has(i) ? <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> : <RefreshCw size={14} />}
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => triggerFileUpload(i)}
                disabled={uploadingCardIndex === i}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-purple-500 hover:text-purple-600 transition-colors font-medium text-xs"
              >
                {uploadingCardIndex === i ? <span className="spinner inline-block w-3 h-3 border-2 rounded-full animate-spin" /> : <ImagePlus size={14} />}
                {t("deckModal.addPhoto")}
              </button>
              <button
                type="button"
                onClick={() => handleGenerateAIImage(i)}
                disabled={generatingAICardIndices.has(i)}
                className="flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 hover:border-purple-500 hover:text-purple-600 transition-colors font-medium text-xs disabled:opacity-50"
              >
                {generatingAICardIndices.has(i) ? <span className="spinner inline-block w-3 h-3 border-2 rounded-full animate-spin" /> : <Sparkles size={14} />}
                {t("deckModal.aiImage")}
              </button>
            </div>
            <button onClick={() => removeCard(i)} style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 14, padding: 8, flexShrink: 0 }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "hsl(var(--accent3))")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "hsl(var(--muted-foreground))")}
            >✕</button>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => handleSave(false)} style={{ flex: 1, padding: "10px 16px", borderRadius: 50, border: "none", background: "hsl(var(--primary))", color: "white", fontFamily: "DM Sans, sans-serif", fontSize: 14, fontWeight: 500, cursor: "pointer" }}>
          💾 {t("deckModal.save")}
        </button>
        <button onClick={onClose} style={{ ...ghostBtnSm, padding: "10px 16px", fontSize: 14 }}>{t("deckModal.cancel")}</button>
      </div>

      {previewImage &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
            onClick={() => setPreviewImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label={t("deckModal.imagePreview")}
          >
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 z-[101] flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
              aria-label={t("common.close")}
            >
              <X size={24} strokeWidth={2} />
            </button>
            <div
              className="relative max-w-3xl max-h-[80vh] w-full h-full flex justify-center items-center"
              onClick={(e) => e.stopPropagation()}
              role="presentation"
            >
              <img
                src={previewImage}
                alt="Preview"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>,
          document.body
        )}
      <ImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={(imported) => {
          setCards((prev) => {
            const validExisting = prev.filter((c) => (c.front || "").trim() !== "" || (c.back || "").trim() !== "");
            const sourceLang = prev[0] ?? validExisting[0] ?? prev[prev.length - 1];
            const inheritedFrontLang = sourceLang?.frontLang ?? "";
            const inheritedBackLang = sourceLang?.backLang ?? "";
            return [
              ...validExisting,
              ...imported.map((c) => ({
                ...c,
                frontLang: (c.frontLang ?? "").trim() || inheritedFrontLang,
                backLang: (c.backLang ?? "").trim() || inheritedBackLang,
              })),
            ];
          });
          toast.success(`${imported.length} ${t("deckModal.cardsImported")}`);
        }}
      />
    </>
  );
});

// ─── AI Tab ───────────────────────────────────────────────────────────────────

function AITab({
  initialType,
  onSave,
  onClose,
  aiModel,
  pollinationsKey,
  geminiKey,
  onAIGenerationComplete,
  onAutomationDraftChange,
  onSourceSignatureChange,
}: {
  initialType?: "words" | "cards" | "tests";
  onSave: (d: Omit<Deck, "id">, options?: { isFromAi?: boolean }) => void;
  onClose: () => void;
  aiModel: string;
  pollinationsKey: string[];
  /** Gemini API keys — required for simple topic-based AI generation (Generate button) */
  geminiKey: string[];
  /** Called when AI text generation completes — switches to Manual tab with generated cards */
  onAIGenerationComplete?: (payload: AutomationPrefill) => void;
  /** Reactive structured draft extracted from text/image input. */
  onAutomationDraftChange?: (payload: AutomationPrefill | null) => void;
  /** Current AI source signature, used to invalidate older generated results when input changes. */
  onSourceSignatureChange?: (signature: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const uiLangCode = (i18n.language || "en").split("-")[0];
  const defaultTargetLang = STUDY_LANGUAGE_CODES.includes(uiLangCode) ? uiLangCode : "en";
  const [sourceText, setSourceText] = useState("");
  const [genLang, setGenLang] = useState<string>("en");
  const [genTargetLang, setGenTargetLang] = useState<string>(defaultTargetLang);
  const [genCount, setGenCount] = useState<number | "">(15);
  const [genType, setGenType] = useState("auto");
  const [genLevel, setGenLevel] = useState<string>("auto");
  const [loading, setLoading] = useState(false);
  const [genProgress, setGenProgress] = useState<{ current: number; total: number } | null>(null);
  const [pendingCards, setPendingCards] = useState<Card[] | null>(null);
  const [deckTitle, setDeckTitle] = useState("");
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [countTouched, setCountTouched] = useState(false);
  const [sourceLangTouched, setSourceLangTouched] = useState(false);
  const [targetLangTouched, setTargetLangTouched] = useState(false);
  const lastAutoCountRef = useRef<number | null>(null);
  const lastAutoSourceLangRef = useRef<string | null>(null);
  const lastAutoTargetLangRef = useRef<string | null>(null);
  const lastAutoTitleRef = useRef("");
  const deferredSourceText = useDeferredValue(sourceText);
  const parsedInputMulti = useMemo(() => parseFlashcardInputMulti(deferredSourceText), [deferredSourceText]);
  const parsedInput = useMemo(() => parseFlashcardInput(deferredSourceText), [deferredSourceText]);

  useEffect(() => {
    onSourceSignatureChange?.(parsedInputMulti.sourceSignature);
  }, [onSourceSignatureChange, parsedInputMulti.sourceSignature]);

  useEffect(() => {
    if (!parsedInputMulti.sourceText) {
      lastAutoCountRef.current = null;
      lastAutoSourceLangRef.current = null;
      lastAutoTargetLangRef.current = null;
      lastAutoTitleRef.current = "";
      onAutomationDraftChange?.(null);
      return;
    }

    const isStructured = parsedInputMulti.totalCount > 0;
    if (!isStructured) {
      onAutomationDraftChange?.(null);
      return;
    }

    if (
      parsedInputMulti.totalCount > 0 &&
      (!countTouched || genCount === "" || Number(genCount) === lastAutoCountRef.current)
    ) {
      setGenCount(parsedInputMulti.totalCount);
    }
    lastAutoCountRef.current = parsedInputMulti.totalCount;

    const firstMod = parsedInputMulti.modules[0];
    if (
      firstMod?.sourceLang &&
      (!sourceLangTouched || genLang === lastAutoSourceLangRef.current)
    ) {
      setGenLang(firstMod.sourceLang);
    }
    if (firstMod?.sourceLang) {
      lastAutoSourceLangRef.current = firstMod.sourceLang;
    }

    if (
      firstMod?.targetLang &&
      (!targetLangTouched || genTargetLang === lastAutoTargetLangRef.current)
    ) {
      setGenTargetLang(firstMod.targetLang);
    }
    if (firstMod?.targetLang) {
      lastAutoTargetLangRef.current = firstMod.targetLang;
    }

    if (firstMod?.title && !parsedInputMulti.hasMultipleTopics) {
      setDeckTitle((prev) => {
        const trimmedPrev = prev.trim();
        if (!trimmedPrev || trimmedPrev === lastAutoTitleRef.current) {
          lastAutoTitleRef.current = firstMod.title;
          return firstMod.title;
        }
        return prev;
      });
    }
  }, [
    countTouched,
    genCount,
    genLang,
    genTargetLang,
    onAutomationDraftChange,
    parsedInputMulti,
    sourceLangTouched,
    targetLangTouched,
  ]);

  const structuredPrefill = useMemo<AutomationPrefill | null>(() => {
    if (parsedInputMulti.totalCount === 0) return null;
    if (parsedInputMulti.hasMultipleTopics && parsedInputMulti.modules.length > 0) {
      return {
        cards: [],
        sourceSignature: parsedInputMulti.sourceSignature,
        sourceKind: "parsed",
        modules: parsedInputMulti.modules.map((m: ParsedModule) => ({
          title: m.title,
          subtitle: m.subtitle,
          cards: pairsToCardDrafts(m.pairs, m.sourceLang ?? genLang, m.targetLang ?? genTargetLang),
          sourceLang: m.sourceLang ?? genLang,
          targetLang: m.targetLang ?? genTargetLang,
        })),
      };
    }
    const single = parsedInput;
    if (!single.isStructured || single.pairs.length === 0) return null;
    return {
      cards: pairsToCardDrafts(single.pairs, genLang, genTargetLang),
      title: single.title,
      lang: genLang,
      level: genLevel !== "auto" ? genLevel : undefined,
      sourceLang: genLang,
      targetLang: genTargetLang,
      count: single.count,
      sourceSignature: single.sourceSignature,
      sourceKind: "parsed",
    };
  }, [genLang, genLevel, genTargetLang, parsedInput, parsedInputMulti]);

  useEffect(() => {
    onAutomationDraftChange?.(structuredPrefill);
  }, [onAutomationDraftChange, structuredPrefill]);

  /** Call Gemini API to generate vocabulary cards, then switch to Manual tab and trigger image gen. Admin key used. */
  const handleAIGeneration = async () => {
    const topic = sourceText.trim();
    if (!topic) {
      setError(t("deckModal.enterTextOrPrompt"));
      return;
    }

    if (structuredPrefill?.modules?.length) {
      onAIGenerationComplete?.({
        ...structuredPrefill,
        modules: structuredPrefill.modules,
        autoGenerateImages: true,
      });
      return;
    }
    if (structuredPrefill?.cards?.length) {
      const requestedCount = Math.max(Number(genCount) || structuredPrefill.cards.length, 1);
      const cards = structuredPrefill.cards.slice(0, Math.min(requestedCount, structuredPrefill.cards.length));
      onAIGenerationComplete?.({
        ...structuredPrefill,
        cards,
        count: cards.length,
        autoGenerateImages: true,
      });
      return;
    }

    const topicBlocks = segmentTextForMultiTopicAI(topic);
    if (topicBlocks.length >= 2) {
      setLoading(true);
      setError("");
      const perBlockCount = Math.max(Math.floor((Number(genCount) || 15) / topicBlocks.length), 3);
      const totalEstimate = topicBlocks.length * perBlockCount;
      setGenProgress({ current: 0, total: totalEstimate });
      try {
        const keysFromProps = geminiKey ?? [];
        const keysFromStorage =
          typeof localStorage !== "undefined"
            ? (() => {
                try {
                  const raw = localStorage.getItem("ld_gemini");
                  if (!raw?.trim()) return [];
                  const parsed = raw.trim().startsWith("[") ? JSON.parse(raw) : [raw];
                  return Array.isArray(parsed) ? parsed.filter((k: unknown) => typeof k === "string" && k.trim()) : [];
                } catch {
                  return [];
                }
              })()
            : [];
        const mergedKeys = keysFromProps.length > 0 ? keysFromProps : keysFromStorage;
        const sourceLangName = GEMINI_LANG_NAMES[genLang] || genLang;
        const targetLangName = GEMINI_LANG_NAMES[genTargetLang] || genTargetLang;
        const levelStr = genLevel && genLevel !== "auto" ? ` CEFR level: ${genLevel}.` : "";
        const systemInstruction = `Return ONLY a JSON array. No markdown blocks, no explanations. Each object: word (in source lang), translation (in target lang), hint, imagePrompt (1-2 word English, underscores).`;
        const modules: Array<{ title: string; subtitle?: string; cards: Card[]; sourceLang?: string; targetLang?: string }> = [];
        let done = 0;
        for (let i = 0; i < topicBlocks.length; i++) {
          const block = topicBlocks[i];
          const pairs = extractStructuredPairs(block.rawText);
          let blockCards: Card[];
          if (pairs.length > 0) {
            blockCards = pairsToCardDrafts(pairs).slice(0, perBlockCount);
          } else {
            const userPrompt = `Generate ${perBlockCount} vocabulary items from this content. Section: ${block.title}.\n\nContent:\n${block.rawText.slice(0, 1500)}\n\nWord in ${sourceLangName}, translation in ${targetLangName}.${levelStr} Output JSON array: [{"word":"","translation":"","hint":"","imagePrompt":""}]`;
            const rawText = await callGemini(
              userPrompt,
              mergedKeys,
              t("deckModal.emptyAiResponse"),
              { systemInstruction, maxOutputTokens: 2000, temperature: 0.7 }
            );
            const parsed = extractJson(rawText);
            const rawItems = Array.isArray(parsed) ? parsed : [];
            blockCards = rawItems
              .filter((item: unknown) => item && typeof item === "object")
              .map((item: Record<string, unknown>) => ({
                front: String(item.word ?? "").trim(),
                back: String(item.translation ?? "").trim(),
                hint: item.hint != null ? String(item.hint).trim() : undefined,
                imagePrompt: item.imagePrompt != null ? String(item.imagePrompt).trim().replace(/\s+/g, "_") : undefined,
              }))
              .filter((c: { front: string; back: string }) => c.front && c.back);
          }
          modules.push({
            title: block.title,
            subtitle: block.subtitle,
            cards: blockCards,
            sourceLang: genLang,
            targetLang: genTargetLang,
          });
          done += blockCards.length;
          setGenProgress({ current: done, total: totalEstimate });
        }
        if (modules.every((m) => m.cards.length === 0)) {
          throw new Error(t("deckModal.cardsNotGenerated"));
        }
        onAIGenerationComplete?.({
          cards: [],
          sourceSignature: topic.toLowerCase(),
          sourceKind: "ai",
          modules: modules.filter((m) => m.cards.length > 0),
          autoGenerateImages: true,
        });
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : String(e);
        console.error("[DeckModal] handleAIGeneration multi-topic failed:", e);
        toast.error(errMsg || t("deckModal.generationError"));
        setError(errMsg || t("deckModal.generationError"));
      } finally {
        setLoading(false);
        setGenProgress(null);
      }
      return;
    }

    const quantity = Math.min(Math.max(Number(genCount) || 15, 1), 100);
    const sourceLangName = GEMINI_LANG_NAMES[genLang] || genLang;
    const targetLangName = GEMINI_LANG_NAMES[genTargetLang] || genTargetLang;
    const levelStr = genLevel && genLevel !== "auto" ? ` CEFR level: ${genLevel}.` : "";

    setLoading(true);
    setError("");
    setGenProgress({ current: 0, total: quantity });

    const systemInstruction = `Return ONLY a JSON array. No markdown blocks, no explanations. Each object: word (in source lang), translation (in target lang), hint, imagePrompt (1-2 word English, underscores).`;

    const userPrompt = `Generate ${quantity} vocabulary items. Topic: ${topic}. Word in ${sourceLangName}, translation in ${targetLangName}.${levelStr} Output JSON array: [{"word":"","translation":"","hint":"","imagePrompt":""}]`;

    try {
      const keysFromProps = geminiKey ?? [];
      const keysFromStorage =
        typeof localStorage !== "undefined"
          ? (() => {
              try {
                const raw = localStorage.getItem("ld_gemini");
                if (!raw?.trim()) return [];
                const parsed = raw.trim().startsWith("[") ? JSON.parse(raw) : [raw];
                return Array.isArray(parsed) ? parsed.filter((k: unknown) => typeof k === "string" && k.trim()) : [];
              } catch {
                return [];
              }
            })()
          : [];
      const mergedKeys = keysFromProps.length > 0 ? keysFromProps : keysFromStorage;
      const rawText = await callGemini(
        userPrompt,
        mergedKeys,
        t("deckModal.emptyAiResponse"),
        { systemInstruction, maxOutputTokens: 4000, temperature: 0.7 }
      );

      const parsed = extractJson(rawText);
      const rawItems = Array.isArray(parsed) ? parsed : [];

      const cards: Card[] = rawItems
        .filter((item: unknown) => item && typeof item === "object")
        .map((item: Record<string, unknown>) => ({
          front: String(item.word ?? "").trim(),
          back: String(item.translation ?? "").trim(),
          hint: item.hint != null ? String(item.hint).trim() : undefined,
          imagePrompt: item.imagePrompt != null ? String(item.imagePrompt).trim().replace(/\s+/g, "_") : undefined,
        }))
        .filter((c) => c.front && c.back);

      if (cards.length === 0) {
        throw new Error(t("deckModal.cardsNotGenerated"));
      }

      onAIGenerationComplete?.({
        cards,
        title: parsedInput.title,
        lang: genLang,
        level: genLevel !== "auto" ? genLevel : undefined,
        sourceLang: genLang,
        targetLang: genTargetLang,
        count: cards.length,
        sourceSignature: parsedInput.sourceSignature,
        sourceKind: "ai",
        autoGenerateImages: true,
      });
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      console.error("[DeckModal] handleAIGeneration failed:", e, "message:", errMsg);
      toast.error(errMsg || t("deckModal.generationError"));
      setError(errMsg || t("deckModal.generationError"));
    } finally {
      setLoading(false);
      setGenProgress(null);
    }
  };

  const handleFile = async (file: File) => {
    setFileName(t("deckModal.readingFile", { file: file.name }));
    try {
      const text = await extractTextFromSupportedFile(file, { geminiApiKeys: geminiKey ?? [], t });
      const cleaned = text.trim().slice(0, 12000);
      setSourceText(cleaned);
      setFileName(t("deckModal.fileReady", { file: file.name, count: text.length }));
    } catch (e: any) {
      setFileName(t("deckModal.fileError", { message: e.message }));
    }
  };

  const generateCards = async () => {
    if (!sourceText.trim()) { setError(t("deckModal.enterTextOrPrompt")); return; }
    setLoading(true);
    setGenProgress(null);
    setError("");

    const typeInstructions: Record<string, string> = {
      gap: `Create fill-in-the-gap flashcards. Front = sentence with ______ blank (in ${genLang}), Back = the missing word(s) (in ${genTargetLang}), Hint = first letter(s).`,
      vocab: `Create vocabulary flashcards. Front = word/phrase in ${genLang}, Back = translation/definition in ${genTargetLang}, Hint = first letter. When generating nouns, ALWAYS include the appropriate definite or indefinite article based on the target language (e.g., "la pomme" not "pomme", "der Apfel" not "Apfel", "the apple" not "apple").`,
      grammar: `Create grammar flashcards. Front = sentence with ______ (in ${genLang}), Back = correct form (in ${genTargetLang}), Hint = rule briefly.`,
      translate: `Create translation flashcards. Front = phrase in ${genLang}, Back = translation in ${genTargetLang}. When generating nouns, ALWAYS include the appropriate definite or indefinite article based on the target language.`,
      auto: `Analyze the content and choose the best flashcard type automatically. Front in ${genLang}, Back in ${genTargetLang}. When generating nouns, ALWAYS include the appropriate definite or indefinite article based on the target language.`,
    };

    const levelInstruction =
      genLevel && genLevel !== "auto"
        ? `\n\nIMPORTANT: The content MUST strictly target the ${genLevel} CEFR proficiency level. Use vocabulary, grammar structures, and sentence complexity appropriate only for ${genLevel} learners.`
        : "";

    const isPrompt = sourceText.length < 300 && /crea|genera|make|create|give|дай|создай|сгенерируй/i.test(sourceText);
    const targetLanguages = I18N_LANGUAGE_KEYS;
    const langsList = targetLanguages.join(", ");
    const jsonSchemaInstruction = `You MUST return a JSON OBJECT containing a "cards" array. Each card MUST have: front, back, hint, and test_data. Target languages: [${langsList}]. For EVERY card generate test_data for each language: { "en": { "question": "...", "options": ["a","b","c","d"], "correctIndex": 0-3, "hint": "..." }, "ru": {...}, "es": {...}, "fr": {...}, "uk": {...} } (include all listed languages). correctIndex = index of correct option (= back). Return ONLY valid JSON, no markdown or conversational text.`;
    const basePrompt = isPrompt
      ? `You are a language teacher assistant. ${sourceText}\nCreate flashcards: Front in ${genLang}, Back (translation) in ${genTargetLang}.\n${typeInstructions[genType]}${levelInstruction}\n${jsonSchemaInstruction}`
      : `You are a language teacher. Analyze this content and create flashcards: Front in ${genLang}, Back (translation) in ${genTargetLang}.\n${typeInstructions[genType]}${levelInstruction}\n\nContent:\n${sourceText.slice(0, 3500)}\n\n${jsonSchemaInstruction}`;

    const totalToGenerate = Math.min(Math.max(Number(genCount) || 15, 1), 100);
    const batchSize = CARDS_PER_BATCH;
    const numBatches = Math.ceil(totalToGenerate / batchSize);
    let accumulatedCards: Card[] = [];
    const alreadyGeneratedFronts: string[] = [];

    try {
      for (let batchIndex = 0; batchIndex < numBatches; batchIndex++) {
        const remaining = totalToGenerate - accumulatedCards.length;
        const countThisBatch = Math.min(batchSize, remaining);
        setGenProgress({ current: accumulatedCards.length, total: totalToGenerate });

        let batchPrompt: string;
        if (batchIndex === 0) {
          batchPrompt = `${basePrompt}\nCreate exactly ${countThisBatch} flashcards. Each card MUST include full test_data for all languages: [${targetLanguages.join(", ")}].`;
        } else {
          const excludeList = alreadyGeneratedFronts.slice(-50).join(", ");
          const levelPart = genLevel && genLevel !== "auto" ? ` CEFR level: ${genLevel}.` : "";
          batchPrompt = `Generate exactly ${countThisBatch} more flashcards. Front in ${genLang}, Back in ${genTargetLang}. Type: ${genType}.${levelPart}\n\nIMPORTANT: Do NOT generate these words/phrases again: [${excludeList}]\n\nUse the same strict multi-language JSON schema: ${jsonSchemaInstruction}\n\nReturn ONLY a valid JSON object: {"cards":[{...},...]}. No empty text or conversational filler.`;
        }

        if (batchIndex > 0) {
          await new Promise((resolve) => setTimeout(resolve, 4000));
        }

        const maxRetries = 3;
        let rawText: string = "";
        let parsed: unknown = null;
        let lastErr: unknown = null;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            rawText = await callAI(batchPrompt, pollinationsKey, aiModel, geminiKey ?? []);
            console.log("RAW AI RESPONSE:", rawText);
            if (!rawText || rawText.trim().length < 2) {
              throw new Error("AI returned empty response");
            }
            parsed = extractJson(rawText);
            break;
          } catch (err: unknown) {
            lastErr = err;
            if (attempt < maxRetries) {
              console.warn(`[DeckModal] Batch ${batchIndex + 1} attempt ${attempt}/${maxRetries} failed, retrying...`, err);
            }
          }
        }

        if (parsed === null) {
          const msg = lastErr instanceof Error ? lastErr.message : String(lastErr);
          console.error("[DeckModal] Batch", batchIndex + 1, "failed after retries:", lastErr);
          if (accumulatedCards.length > 0) {
            setError(t("deckModal.aiStoppedEarly", { count: accumulatedCards.length }) || `AI stopped early. Generated ${accumulatedCards.length} cards.`);
          } else {
            setError(msg || t("deckModal.generationError"));
          }
          break;
        }

        const batchCards: Card[] = Array.isArray(parsed)
          ? parsed as Card[]
          : parsed && typeof parsed === "object" && "cards" in parsed
            ? (parsed as { cards: Card[] }).cards
            : [parsed] as Card[];
        if (!batchCards.length) {
          if (accumulatedCards.length > 0) {
            setError(t("deckModal.aiStoppedEarly", { count: accumulatedCards.length }) || `AI stopped early. Generated ${accumulatedCards.length} cards.`);
            break;
          }
          throw new Error(t("deckModal.cardsNotGenerated"));
        }

        for (const c of batchCards) {
          if (c?.front != null && c?.back != null) {
            accumulatedCards.push({
              front: String(c.front).trim(),
              back: String(c.back).trim(),
              hint: c.hint != null ? String(c.hint).trim() : undefined,
              testData: normalizeTestData((c as { test_data?: unknown }).test_data, I18N_LANGUAGE_KEYS),
            });
            alreadyGeneratedFronts.push(String(c.front).trim());
          }
        }

        setPendingCards([...accumulatedCards]);
        setGenProgress({ current: accumulatedCards.length, total: totalToGenerate });
      }

      setGenProgress(null);
      if (!deckTitle) {
        setDeckTitle(`${LANG_FLAGS[genLang] || "🤖"} ${t("deckModal.aiDeckDefault")} ${new Date().toLocaleDateString()}`);
      }
    } catch (e: unknown) {
      const errMsg = e instanceof Error ? e.message : String(e);
      console.error("[DeckModal] generateCards error:", e, "message:", errMsg);
      toast.error(errMsg || t("deckModal.generationError"));
      setError(errMsg || t("deckModal.generationError"));
      setPendingCards(null);
    } finally {
      setLoading(false);
      setGenProgress(null);
    }
  };

  const handleSave = () => {
    if (!pendingCards || pendingCards.length === 0) return;
    const title = deckTitle.trim() || `${t("deckModal.aiDeckDefault")} ${new Date().toLocaleDateString()}`;
    const deckType = initialType ? INITIAL_TYPE_TO_DECK_TYPE[initialType] : genType;
    onSave(
      {
        title,
        lang: genLang as DeckLang,
        emoji: LANG_FLAGS[genLang] || "🤖",
        cards: pendingCards,
        deckType,
        type: (initialType ?? "cards") as "words" | "cards" | "tests",
      },
      { isFromAi: true }
    );
  };

  return (
    <>
      {/* Premium glassmorphism container */}
      <div className="rounded-2xl sm:rounded-3xl p-4 sm:p-5 mb-4 backdrop-blur-sm bg-white/70 dark:bg-slate-900/70 border border-white/50 dark:border-slate-500/30 shadow-lg shadow-slate-300/40 dark:shadow-slate-950/50">
        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          onClick={() => fileRef.current?.click()}
          className={`rounded-xl py-3 px-4 sm:py-3.5 sm:px-5 text-center cursor-pointer transition-all mb-3 border-2 border-dashed ${
            dragOver ? "border-[hsl(var(--primary))] bg-primary/5" : "border-[hsl(var(--border))] bg-black/[0.02] dark:bg-white/[0.03]"
          }`}
        >
          <div className="text-2xl sm:text-[26px] mb-1">📂</div>
          <div className="text-xs sm:text-[13px] font-semibold mb-0.5">{t("deckModal.uploadFile")}</div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t("deckModal.fileFormatsExtended")}</div>
          {fileName && (
            <div className="mt-2 bg-primary/15 border border-primary/40 rounded-full px-3 py-1 text-[11px] text-primary inline-block">
              {fileName}
            </div>
          )}
          <input ref={fileRef} type="file" accept=".txt,.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
        </div>

        <div className="flex items-center gap-2.5 mb-3 text-[hsl(var(--muted-foreground))] text-[11px]">
          <div className="flex-1 h-px bg-[hsl(var(--border))]/60" />
          {t("deckModal.orPasteText")}
          <div className="flex-1 h-px bg-[hsl(var(--border))]/60" />
        </div>

        {/* Textarea — refined inputs */}
        <div className="mb-3">
          <label className="block text-[11px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">{t("deckModal.textOrPrompt")}</label>
          <textarea
            value={sourceText}
            onChange={(e) => setSourceText(e.target.value)}
            rows={5}
            placeholder={t("deckModal.textPlaceholder")}
            className="w-full min-h-[100px] resize-y rounded-xl px-4 py-3.5 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/70 focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 focus:border-[hsl(var(--primary))]/50 transition-all font-[inherit] text-[13px] sm:text-[14px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40"
            style={{ fontFamily: "DM Sans, sans-serif", boxSizing: "border-box" }}
          />
          {parsedInputMulti.hasMultipleTopics && parsedInputMulti.modules.length > 0 && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/30 text-sm text-purple-800 dark:text-purple-200">
              ✨ {t("deckModal.topicsFound", { count: parsedInputMulti.modules.length })} — {t("deckModal.topicsFoundSubtitle")}
            </div>
          )}
        </div>

        {/* AI settings — strict 2-col grid; all h-11; full-width Generate at bottom */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-4 mb-4">
          <div className="flex flex-col gap-1.5 min-w-0">
            <label className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {t("deckModal.sourceLanguage")}
            </label>
            <select
              value={genLang}
              onChange={(e) => {
                setSourceLangTouched(true);
                setGenLang(e.target.value);
              }}
              className="w-full h-11 rounded-xl px-3 py-2.5 text-[13px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 font-[inherit]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {STUDY_LANGUAGE_CODES.map((code) => (
                <option key={code} value={code}>{LANG_NAMES[code]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <label className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {t("deckModal.targetLanguage")}
            </label>
            <select
              value={genTargetLang}
              onChange={(e) => {
                setTargetLangTouched(true);
                setGenTargetLang(e.target.value);
              }}
              className="w-full h-11 rounded-xl px-3 py-2.5 text-[13px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 font-[inherit]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              {STUDY_LANGUAGE_CODES.map((code) => (
                <option key={code} value={code}>{LANG_NAMES[code]}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <label className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {t("deckModal.type")}
            </label>
            <select
              value={genType}
              onChange={(e) => setGenType(e.target.value)}
              className="w-full h-11 rounded-xl px-3 py-2.5 text-[13px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 font-[inherit]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <option value="gap">{t("deckModal.typeGap")}</option>
              <option value="vocab">{t("deckModal.typeVocab")}</option>
              <option value="grammar">{t("deckModal.typeGrammar")}</option>
              <option value="translate">{t("deckModal.typeTranslation")}</option>
              <option value="auto">{t("deckModal.autoType")}</option>
            </select>
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <label className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {t("deckModal.count")}
            </label>
            <input
              type="number"
              value={genCount}
              min={1}
              max={100}
              onChange={(e) => {
                const v = e.target.value;
                setCountTouched(true);
                if (v === "") { setGenCount(""); return; }
                const n = parseInt(v, 10);
                if (!isNaN(n)) setGenCount(n);
              }}
              onBlur={() => {
                const num = Number(genCount);
                if (genCount === "" || num === 0 || isNaN(num)) setGenCount(15);
                else {
                  const clamped = Math.min(Math.max(num, 1), 100);
                  if (clamped !== num) setGenCount(clamped);
                }
              }}
              className="w-full h-11 rounded-xl px-3 py-2.5 text-[13px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 font-[inherit]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            />
          </div>
          <div className="flex flex-col gap-1.5 min-w-0">
            <label className="text-[10px] font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
              {t("deckModal.level")}
            </label>
            <select
              value={genLevel}
              onChange={(e) => setGenLevel(e.target.value)}
              className="w-full h-11 rounded-xl px-3 py-2.5 text-[13px] border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/40 font-[inherit]"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              <option value="auto">{t("deckModal.levelAuto")}</option>
              {["A1", "A2", "B1", "B2", "C1", "C2"].map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5 min-w-0 items-end justify-end">
            <button
              type="button"
              onClick={() => {
                setSourceText("");
                setFileName("");
                setError("");
                setDeckTitle("");
                setCountTouched(false);
                setSourceLangTouched(false);
                setTargetLangTouched(false);
              }}
              disabled={!sourceText && !fileName}
              className="flex items-center justify-center w-11 h-11 rounded-xl border border-[hsl(var(--border))]/60 bg-white/60 dark:bg-slate-800/40 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--bg3))]/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
              title={t("undo")}
              aria-label={t("undo")}
            >
              <RotateCcw size={16} strokeWidth={2} />
            </button>
          </div>
          <button
            onClick={handleAIGeneration}
            disabled={loading}
            className="col-span-2 w-full h-11 flex items-center justify-center gap-2 px-6 rounded-xl font-medium text-sm bg-[hsl(var(--primary))] text-white hover:opacity-95 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-md shadow-purple-500/25"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {loading ? (
              <span className="spinner" style={{ width: 18, height: 18, borderWidth: 2, borderColor: "rgba(255,255,255,.3)", borderTopColor: "white" }} />
            ) : (
              "✨"
            )}{" "}
            {loading && genProgress
              ? t("deckModal.generatingWordsProgress", { current: genProgress.current, total: genProgress.total })
              : t("deckModal.generateWords")}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: "rgba(255,180,0,.08)", border: "1px solid rgba(255,180,0,.3)", borderRadius: "var(--radius-sm)", padding: "10px 14px", fontSize: 13, marginBottom: 12 }}>
          ❌ {error}
        </div>
      )}

      {/* Generated cards preview */}
      {pendingCards && (
        <div style={{ background: "hsl(var(--bg3))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius-sm)", padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
            🎴 {t("deckModal.cardsGenerated", { count: pendingCards.length })}
          </div>

          {/* Deck title input */}
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>{t("deckModal.deckName")}</label>
            <input
              value={deckTitle}
              onChange={(e) => setDeckTitle(e.target.value)}
              placeholder={t("deckModal.deckNamePlaceholderAi")}
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5, maxHeight: 200, overflowY: "auto", marginBottom: 12 }}>
            {pendingCards.map((c, i) => (
              <div key={i} style={{ background: "hsl(var(--bg2))", borderRadius: "var(--radius-sm)", padding: "8px 12px", fontSize: 12, display: "flex", alignItems: "flex-start", gap: 8 }}>
                <span style={{ color: "hsl(var(--muted-foreground))", fontSize: 10, minWidth: 18, paddingTop: 1 }}>{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>{c.front}</span>
                  <span style={{ color: "hsl(var(--muted-foreground))" }}> → </span>
                  <span style={{ color: "hsl(var(--accent2))", fontWeight: 600 }}>{c.back}</span>
                  {c.hint && <span style={{ color: "hsl(var(--muted-foreground))", fontSize: 10, marginLeft: 4 }}>({c.hint})</span>}
                  {c.testData && Object.keys(c.testData).length > 0 && (
                    <span title={t("deckModalExt.testData")} style={{ marginLeft: 6, background: "rgba(109,90,255,.2)", color: "hsl(var(--primary))", fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 4 }}>📋</span>
                  )}
                </div>
                <button onClick={() => setPendingCards((prev) => prev ? prev.filter((_, idx) => idx !== i) : null)} style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 11, padding: "1px 3px" }}>✕</button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleSave} style={{ flex: 1, padding: "10px", borderRadius: 50, border: "none", background: "hsl(var(--accent2))", color: "#000", fontFamily: "DM Sans, sans-serif", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              💾 {t("deckModal.saveDeck")}
            </button>
            <button onClick={onClose} style={{ ...ghostBtnSm, padding: "10px 16px", fontSize: 13 }}>{t("deckModal.cancel")}</button>
          </div>
        </div>
      )}

      {!pendingCards && (
        <button onClick={onClose} style={{ ...ghostBtnSm, width: "100%", padding: "10px", fontSize: 13, textAlign: "center" }}>{t("deckModal.cancel")}</button>
      )}
    </>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

type TFunc = (key: string, opts?: Record<string, unknown>) => string;

function getNewDeckTitle(t: TFunc, deckType?: DeckInitialType): string {
  switch (deckType) {
    case "words":
      return t("deckModal.newDeckWords");
    case "cards":
      return t("deckModal.newDeckCards");
    case "tests":
      return t("deckModal.newDeckTests");
    default:
      return t("deckModal.newDeck");
  }
}

// ─── Main Modal ───────────────────────────────────────────────────────────────

export default function DeckModal({ deck, initialType, onSave, onSaveMultiple, onClose, aiModel, pollinationsKey, geminiKey, asPage, showValidationErrors, setShowValidationErrors }: DeckModalProps) {
  const { t } = useTranslation();
  const manualTabRef = useRef<ManualTabHandle>(null);
  // If editing an existing deck, always show manual tab (no AI tab needed)
  const isEdit = !!deck;
  const [activeTab, setActiveTab] = useState<"manual" | "ai">("manual");
  const [aiSourceSignature, setAiSourceSignature] = useState("");
  const [aiStructuredPrefill, setAiStructuredPrefill] = useState<AutomationPrefill | null>(null);
  const [aiGeneratedPrefill, setAiGeneratedPrefill] = useState<AutomationPrefill | null>(null);

  const hasAI = !isEdit && (
    USE_AI_PROXY ||
    (Array.isArray(geminiKey) ? geminiKey.some((k) => k?.trim()) : !!geminiKey?.length) ||
    (!!aiModel && (Array.isArray(pollinationsKey) ? pollinationsKey.some((k) => k?.trim()) : !!pollinationsKey?.length))
  );

  const handleAIGenerationComplete = (payload: AutomationPrefill) => {
    setAiGeneratedPrefill(payload);
    setActiveTab("manual");
  };

  const manualPrefill =
    aiGeneratedPrefill && aiGeneratedPrefill.sourceSignature === aiSourceSignature
      ? aiGeneratedPrefill
      : (aiStructuredPrefill?.cards?.length || aiStructuredPrefill?.modules?.length)
        ? aiStructuredPrefill
        : null;

  const formContent = (
    <div
      className={asPage ? "w-full flex flex-col gap-4 md:gap-8" : "modal-box"}
      style={asPage
        ? { width: "100%", minWidth: 0 }
        : { background: "hsl(var(--bg2))", border: "1px solid hsl(var(--border))", borderRadius: "var(--radius)", padding: 28, width: "100%", maxWidth: 540, maxHeight: "92vh", overflowY: "auto" }
      }
    >
      {/* Page header — back + title + save actions; single back at top only */}
      {asPage && !isEdit && (activeTab === "manual" || !hasAI) && (
        <div className="flex justify-between items-center gap-3 mb-3 md:mb-6 -mt-1">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              type="button"
              onClick={onClose}
              className="flex shrink-0 items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label={t("deckModal.backToHome")}
            >
              <ArrowLeft size={18} strokeWidth={2} />
              {t("deckModal.backToHome")}
            </button>
            <h1 className="text-xl font-bold md:text-3xl text-gray-900 dark:text-[hsl(var(--foreground))] truncate">
              {t("deckModal.createPageTitle")}
            </h1>
          </div>
          <div className="hidden md:flex gap-4 shrink-0">
            <button
              type="button"
              onClick={() => manualTabRef.current?.handleSave(false)}
              className="px-8 py-3 rounded-xl font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors"
            >
              {t("deckModal.create")}
            </button>
            <button
              type="button"
              onClick={() => manualTabRef.current?.handleSave(true)}
              className="px-8 py-3 rounded-xl font-bold bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-md"
            >
              {t("deckModal.createAndStudy")}
            </button>
          </div>
        </div>
      )}

      {/* Header — modal mode or edit mode in page */}
      {(!asPage || isEdit) && (
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-3">
            {asPage && (
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--bg3))] hover:text-[hsl(var(--foreground))] transition-colors"
              >
                <ArrowLeft size={18} strokeWidth={2} />
                {t("deckModal.back")}
              </button>
            )}
            <div className="flex items-center gap-2" style={{ fontSize: 20, fontWeight: 600 }}>
              {isEdit ? (
                <>
                  <Pencil className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
                  {t("deckModal.editDeck")}
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5 text-gray-500 dark:text-gray-400 shrink-0" />
                  {getNewDeckTitle(t, initialType)}
                </>
              )}
            </div>
          </div>
          {asPage && isEdit && (activeTab === "manual" || !hasAI) && (
            <div className="flex items-center gap-3 hidden sm:flex">
              <button
                type="button"
                onClick={() => manualTabRef.current?.handleSave(false)}
                className="px-6 py-2.5 rounded-xl font-bold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-500/30 transition-colors text-sm"
              >
                {deck ? t("deckModal.save") : t("deckModal.create")}
              </button>
              <button
                type="button"
                onClick={() => manualTabRef.current?.handleSave(true)}
                className="px-6 py-2.5 rounded-xl font-bold bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-md text-sm"
              >
                {deck ? t("deckModal.saveAndStudy") : t("deckModal.createAndStudy")}
              </button>
            </div>
          )}
          {!asPage && (
            <button onClick={onClose} style={{ background: "transparent", border: "none", color: "hsl(var(--muted-foreground))", cursor: "pointer", fontSize: 20 }}>✕</button>
          )}
        </div>
      )}

        {/* Tabs — when creating and AI configured */}
        {hasAI && (
          <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "hsl(var(--bg3))", borderRadius: "var(--radius-sm)", padding: 4 }}>
            {(["manual", "ai"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: "calc(var(--radius-sm) - 2px)",
                    border: "none",
                    background: activeTab === tab ? "hsl(var(--bg2))" : "transparent",
                    color: activeTab === tab ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))",
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 13,
                    fontWeight: activeTab === tab ? 600 : 400,
                    cursor: "pointer",
                    transition: "all .15s",
                    boxShadow: activeTab === tab ? "0 1px 3px rgba(0,0,0,.2)" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 6,
                  }}
                >
                  {tab === "manual"
                    ? <><Pencil className="w-4 h-4" />{t("deckModal.manual")}</>
                    : <><Sparkles className="w-4 h-4" />{t("deckModal.aiGenerate")}</>}
                </button>
              ))}
          </div>
        )}

        {/* Tab content */}
        {activeTab === "manual" || isEdit ? (
          <ManualTab
            ref={manualTabRef}
            deck={deck}
            initialType={initialType}
            onSave={onSave}
            onSaveMultiple={onSaveMultiple}
            onClose={onClose}
            prefilledCards={manualPrefill?.modules ? null : (manualPrefill?.cards ?? null)}
            prefilledModules={manualPrefill?.modules ?? null}
            prefilledMeta={manualPrefill
              ? {
                  title: manualPrefill.title ?? manualPrefill.modules?.[0]?.title,
                  lang: manualPrefill.lang ?? manualPrefill.modules?.[0]?.sourceLang,
                  level: manualPrefill.level,
                  sourceLang: manualPrefill.sourceLang ?? manualPrefill.modules?.[0]?.sourceLang,
                  targetLang: manualPrefill.targetLang ?? manualPrefill.modules?.[0]?.targetLang,
                  sourceKind: manualPrefill.sourceKind,
                }
              : null}
            autoGenerateImagesForPrefilled={!!manualPrefill?.autoGenerateImages && !!(manualPrefill.cards?.length || manualPrefill.modules?.some((m) => m.cards?.length))}
            prefillSignature={manualPrefill?.sourceSignature ?? null}
            pollinationsKeys={pollinationsKey ?? []}
            asPage={asPage}
            showValidationErrors={showValidationErrors}
            setShowValidationErrors={setShowValidationErrors}
          />
        ) : (
          <AITab
            initialType={initialType}
            onSave={onSave}
            onClose={onClose}
            aiModel={aiModel ?? "openai"}
            pollinationsKey={pollinationsKey ?? []}
            geminiKey={geminiKey ?? []}
            onAIGenerationComplete={handleAIGenerationComplete}
            onAutomationDraftChange={setAiStructuredPrefill}
            onSourceSignatureChange={setAiSourceSignature}
          />
        )}
    </div>
  );

  if (asPage) {
    return (
      <div className="max-w-5xl mx-auto w-full flex flex-col gap-4 pt-1 px-2 pb-4 sm:pt-4 sm:px-4 sm:pb-6 md:p-8 min-h-0">
        {formContent}
      </div>
    );
  }

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {formContent}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  color: "hsl(var(--muted-foreground))",
  marginBottom: 5,
  textTransform: "uppercase",
  letterSpacing: ".05em",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "hsl(var(--bg3))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "var(--radius-sm)",
  padding: "10px 14px",
  color: "hsl(var(--foreground))",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};

const cardInputStyle: React.CSSProperties = {
  background: "hsl(var(--bg3))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "var(--radius-sm)",
  padding: "8px 12px",
  color: "hsl(var(--foreground))",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 13,
  outline: "none",
  minWidth: 0,
  boxSizing: "border-box",
};

const ghostBtnSm: React.CSSProperties = {
  padding: "6px 12px",
  borderRadius: 50,
  border: "none",
  background: "hsl(var(--bg3))",
  color: "hsl(var(--foreground))",
  fontFamily: "DM Sans, sans-serif",
  fontSize: 12,
  cursor: "pointer",
};

const iconBtnStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  padding: 0,
  borderRadius: "var(--radius-sm)",
  border: "1px solid hsl(var(--border))",
  background: "transparent",
  color: "hsl(var(--muted-foreground))",
  cursor: "pointer",
  transition: "background .15s, color .15s",
};
